/**
 * Generated by orval v8.5.0 🍺
 * Do not edit manually.
 * WorkOS Widgets
 * WorkOS Widgets API
 * OpenAPI spec version: 1.0
 */
import { useMutation, useQuery } from "@tanstack/react-query";
import type {
  DataTag,
  DefinedInitialDataOptions,
  DefinedUseQueryResult,
  MutationFunction,
  QueryClient,
  QueryFunction,
  QueryKey,
  UndefinedInitialDataOptions,
  UseMutationOptions,
  UseMutationResult,
  UseQueryOptions,
  UseQueryResult,
} from "@tanstack/react-query";

export type MemberStatus = (typeof MemberStatus)[keyof typeof MemberStatus];

export const MemberStatus = {
  Active: "Active",
  Invited: "Invited",
  InviteExpired: "InviteExpired",
  InviteRevoked: "InviteRevoked",
  NoInvite: "NoInvite",
} as const;

export type MemberActionsItem =
  (typeof MemberActionsItem)[keyof typeof MemberActionsItem];

export const MemberActionsItem = {
  "edit-role": "edit-role",
  "resend-invite": "resend-invite",
  "revoke-invite": "revoke-invite",
  "revoke-membership": "revoke-membership",
} as const;

export type MemberActions = MemberActionsItem[];

export type MemberRoles =
  | {
      name: string;
      slug: string;
      /** @nullable */
      description?: string | null;
    }[]
  | null;

export interface Member {
  id: string;
  email: string;
  emailVerified: boolean;
  /** @nullable */
  profilePictureUrl?: string | null;
  /** @nullable */
  firstName?: string | null;
  /** @nullable */
  lastName?: string | null;
  /** An ISO 8601 timestamp. */
  createdAt: string;
  /** @nullable */
  lastActivityAt?: string | null;
  status: MemberStatus;
  actions: MemberActions;
  isLoggedInUser?: true | null;
  roles?: MemberRoles;
}

/**
 * Pagination cursors for navigating between pages of results.
 */
export interface ListMetadata {
  /**
   * An object ID that defines your place in the list. When the ID is not present, you are at the start of the list.
   * @nullable
   */
  before: string | null;
  /**
   * An object ID that defines your place in the list. When the ID is not present, you are at the end of the list.
   * @nullable
   */
  after: string | null;
}

export interface MembersResponse {
  data: Member[];
  list_metadata: ListMetadata;
}

export interface MemberRole {
  name: string;
  slug: string;
  default: boolean;
  /** @nullable */
  description?: string | null;
}

export interface RolesAndConfigResponse {
  roles: MemberRole[];
  multipleRolesEnabled: boolean;
}

export interface InviteMemberResponse {
  success: boolean;
}

export interface ResendInviteResponse {
  /** @nullable */
  id?: string | null;
  success: boolean;
}

export interface RevokeMemberResponse {
  id: string;
  success: boolean;
}

export interface UpdateMemberResponse {
  id: string;
  success: boolean;
}

export interface RemoveMemberResponse {
  id: string;
  success: boolean;
}

export interface OrganizationInfo {
  id: string;
  name: string;
  current: boolean;
  /** @nullable */
  favicon?: string | null;
}

export interface OrganizationsResponse {
  data: OrganizationInfo[];
}

export interface OAuthProfile {
  id: string;
  /** @nullable */
  email?: string | null;
  /** @nullable */
  firstName?: string | null;
  /** @nullable */
  lastName?: string | null;
  /** @nullable */
  profilePictureUrl?: string | null;
  /** @nullable */
  lastLoginAt?: string | null;
}

export type MeOauthProfiles = {
  AppleOAuth?: OAuthProfile;
  GithubOAuth?: OAuthProfile;
  GoogleOAuth?: OAuthProfile;
  MicrosoftOAuth?: OAuthProfile;
} | null;

export interface Me {
  id: string;
  email: string;
  /** @nullable */
  firstName?: string | null;
  /** @nullable */
  lastName?: string | null;
  /** @nullable */
  locale?: string | null;
  /** @nullable */
  profilePictureUrl?: string | null;
  oauthProfiles?: MeOauthProfiles;
}

export type CreateTotpFactorResponseAuthenticationFactor = {
  object: "authentication_factor";
  id: string;
  type: "generic_otp" | "sms" | "totp" | "webauthn";
  /** @nullable */
  user_id?: string | null;
  sms?: {
    phone_number: string;
  } | null;
  totp?:
    | {
        issuer: string;
        user: string;
        secret: string;
        qr_code: string;
        uri: string;
      }
    | {
        issuer: string;
        user: string;
      }
    | null;
} & {
  created_at: string;
  updated_at: string;
};

export type CreateTotpFactorResponseAuthenticationChallenge = {
  object: "authentication_challenge";
  id: string;
  /** @nullable */
  expires_at?: string | null;
  /** @nullable */
  code?: string | null;
  authentication_factor_id: string;
} & {
  created_at: string;
  updated_at: string;
};

export interface CreateTotpFactorResponse {
  authenticationFactor: CreateTotpFactorResponseAuthenticationFactor;
  authenticationChallenge: CreateTotpFactorResponseAuthenticationChallenge;
}

export interface VerifyTotpFactorRequest {
  code: string;
  authenticationChallengeId: string;
}

export type AuthenticationInformationResponseDataVerificationMethodsMfa = {
  provider: "MFA";
  isSetUp: boolean;
  /** @nullable */
  lastUsed?: string | null;
} | null;

export type AuthenticationInformationResponseDataVerificationMethodsPassword = {
  provider: "Password";
  isSetUp: boolean;
  /** @nullable */
  lastUsed?: string | null;
  isCurrentSession: boolean;
} | null;

export type AuthenticationInformationResponseDataVerificationMethodsPasskey = {
  provider: "Passkey";
  isSetUp: boolean;
  /** @nullable */
  lastUsed?: string | null;
  passKeys: {
    id: string;
  }[];
  isCurrentSession: boolean;
} | null;

export type AuthenticationInformationResponseDataVerificationMethods = {
  Mfa?: AuthenticationInformationResponseDataVerificationMethodsMfa;
  Password?: AuthenticationInformationResponseDataVerificationMethodsPassword;
  Passkey?: AuthenticationInformationResponseDataVerificationMethodsPasskey;
};

export type AuthenticationInformationResponseDataPasswordSettings = {
  isPasswordNumberRequired: boolean;
  isPasswordPwnedRequired: boolean;
  isPasswordSymbolRequired: boolean;
  isPasswordUppercaseRequired: boolean;
  passwordMinimumLength: number;
  passwordMinimumStrength: number;
};

export type AuthenticationInformationResponseData = {
  verificationMethods: AuthenticationInformationResponseDataVerificationMethods;
  passwordSettings: AuthenticationInformationResponseDataPasswordSettings;
};

export interface AuthenticationInformationResponse {
  data: AuthenticationInformationResponseData;
}

export interface CreatePasswordRequest {
  password: string;
}

export interface UpdatePasswordRequest {
  newPassword: string;
  currentPassword: string;
}

export interface RevokeAllSessionsRequest {
  currentSessionId: string;
}

export type ActiveSessionState = {
  tag: string;
  /** @nullable */
  expiresAt?: string | null;
};

export type ActiveSessionCurrentLocation = {
  cityName: string;
  countryISOCode: string;
} | null;

export interface ActiveSession {
  id: string;
  userlandUserId: string;
  /** @nullable */
  ipAddress?: string | null;
  /** @nullable */
  userAgent?: string | null;
  /** @nullable */
  organizationId?: string | null;
  state: ActiveSessionState;
  currentLocation?: ActiveSessionCurrentLocation;
  usedSsoAuth: boolean;
  usedPasswordAuth: boolean;
  usedPasskeyAuth: boolean;
  usedAppleOauth: boolean;
  usedBitbucketOauth: boolean;
  usedGithubOauth: boolean;
  usedGitLabOauth: boolean;
  usedGoogleOauth: boolean;
  usedLinkedInOauth: boolean;
  usedImpersonation: boolean;
  usedMicrosoftOauth: boolean;
  usedSlackOauth: boolean;
  usedXeroOauth: boolean;
  usedMagicAuth: boolean;
  /** @nullable */
  impersonatorUserId?: string | null;
  /** @nullable */
  impersonatorEmail?: string | null;
  /** @nullable */
  impersonationReason?: string | null;
  /** @nullable */
  lastActivityAt?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ActiveSessionsResponse {
  data: ActiveSession[];
}

export interface SendVerificationResponse {
  authenticationChallenge: string;
  type: "EmailVerification";
}

export interface VerifyRequest {
  code: string;
  authenticationChallengeId: string;
}

export interface VerifyResponse {
  elevatedAccessToken: string;
  expiresAt: string;
}

export interface SendEmailChangeRequest {
  newEmail: string;
}

export interface SendEmailChangeResponse {
  expiresAt: string;
}

export interface VerifyEmailChangeRequest {
  code: string;
}

export interface VerifyEmailChangeResponse {
  user: Me;
  elevatedAccessToken: string;
  expiresAt: string;
}

export type RegisterPasskeyResponseOptions = { [key: string]: unknown };

export interface RegisterPasskeyResponse {
  challengeId: string;
  options: RegisterPasskeyResponseOptions;
}

export type VerifyPasskeyRequestResponse = { [key: string]: unknown };

export interface VerifyPasskeyRequest {
  challengeId: string;
  response: VerifyPasskeyRequestResponse;
}

export interface SettingsResponse {
  object: "settings";
  authkitOrigin: string;
  /** @nullable */
  logoDarkIconPath?: string | null;
  /** @nullable */
  logoDarkPath?: string | null;
  /** @nullable */
  logoLightIconPath?: string | null;
  /** @nullable */
  logoLightPath?: string | null;
  teamName: string;
}

export type OrganizationDomainState =
  (typeof OrganizationDomainState)[keyof typeof OrganizationDomainState];

export const OrganizationDomainState = {
  Failed: "Failed",
  LegacyVerified: "LegacyVerified",
  Pending: "Pending",
  Verified: "Verified",
} as const;

export type DomainVerificationNameServer =
  (typeof DomainVerificationNameServer)[keyof typeof DomainVerificationNameServer];

export const DomainVerificationNameServer = {
  AwsRoute53: "AwsRoute53",
  GoogleDomains: "GoogleDomains",
  CloudFlare: "CloudFlare",
  GoDaddy: "GoDaddy",
  Other: "Other",
} as const;

export interface OrganizationDomain {
  id: string;
  domain: string;
  state: OrganizationDomainState;
  nameServer: DomainVerificationNameServer;
  /** @nullable */
  verificationPrefix?: string | null;
  /** @nullable */
  verificationToken?: string | null;
  /** @nullable */
  subdomain?: string | null;
  createdAt: string;
}

export interface X509CertificateJSON {
  id: string;
  value: string;
  /** @nullable */
  notBefore?: string | null;
  /** @nullable */
  notAfter?: string | null;
  /** @nullable */
  lastExpiryEventSentAt?: string | null;
}

export type SamlSessionState =
  (typeof SamlSessionState)[keyof typeof SamlSessionState];

export const SamlSessionState = {
  Authorized: "Authorized",
  Failed: "Failed",
  Started: "Started",
  Successful: "Successful",
  Timedout: "Timedout",
} as const;

export type OidcSessionState =
  (typeof OidcSessionState)[keyof typeof OidcSessionState];

export const OidcSessionState = {
  Started: "Started",
  Authorized: "Authorized",
  Successful: "Successful",
  Failed: "Failed",
  Terminated: "Terminated",
  Timedout: "Timedout",
} as const;

export const SsoConnectionSessionJSONState = {
  ...SamlSessionState,
  ...OidcSessionState,
} as const;
export interface SsoConnectionSessionJSON {
  id: string;
  createdAt: string;
  state: (typeof SsoConnectionSessionJSONState)[keyof typeof SsoConnectionSessionJSONState];
}

export type SsoConnection =
  | {
      id: string;
      type:
        | "AdfsSaml"
        | "Auth0Saml"
        | "AzureSaml"
        | "CasSaml"
        | "ClassLinkSaml"
        | "CloudflareSaml"
        | "CyberArkSaml"
        | "DuoSaml"
        | "GenericSaml"
        | "GoogleSaml"
        | "JumpCloudSaml"
        | "KeycloakSaml"
        | "LastPassSaml"
        | "MiniOrangeSaml"
        | "NetIqSaml"
        | "OktaSaml"
        | "OneLoginSaml"
        | "OracleSaml"
        | "PingFederateSaml"
        | "PingOneSaml"
        | "RipplingSaml"
        | "SalesforceSaml"
        | "ShibbolethGenericSaml"
        | "ShibbolethSaml"
        | "SimpleSamlPhpSaml"
        | "TestIdp"
        | "VmWareSaml";
      name: string;
      state: "Inactive" | "Validating" | "Active" | "Deleting";
      x509Certificates: X509CertificateJSON[];
      latestExpiringCertificate?: X509CertificateJSON | null;
      latestExpiredCertificate?: X509CertificateJSON | null;
      createdAt: string;
      providerTag: "Saml";
      lastSession?: SsoConnectionSessionJSON | null;
    }
  | ({
      id: string;
      name: string;
      state: "Inactive" | "Validating" | "Active" | "Deleting";
      type:
        | "AdpOidc"
        | "Auth0Migration"
        | "CleverOidc"
        | "EntraIdOidc"
        | "GenericOidc"
        | "GoogleOidc"
        | "OktaOidc"
        | "LoginGovOidc";
      createdAt: string;
      providerTag: "OpenIdConnect";
      lastSession?: SsoConnectionSessionJSON | null;
    } & {
      /** @nullable */
      x509Certificates?: null;
      /** @nullable */
      latestExpiringCertificate?: null;
      /** @nullable */
      latestExpiredCertificate?: null;
    });

export type AuditLogStreamState = "Active" | "Inactive" | "Error" | "Invalid";

export type AuditLogStreamType =
  | "Datadog"
  | "Splunk"
  | "S3"
  | "GoogleCloudStorage"
  | "GenericHttps"
  | "AzureSentinel";

export interface AuditLogStreamJSON {
  id: string;
  state: AuditLogStreamState;
  type: AuditLogStreamType;
  /** @nullable */
  errorMessage?: string | null;
  /** @nullable */
  lastSyncedEventId?: string | null;
  auditLogTrailId: string;
}

export interface ListOrganizationApiKeysResponseData {
  id: string;
  name: string;
  obfuscatedValue: string;
  createdAt: string;
  /** @nullable */
  lastUsedAt?: string | null;
  permissions: string[];
}

export type ListOrganizationApiKeysResponseListMetadata = {
  before?: string;
  after?: string;
};

export interface ListOrganizationApiKeysResponse {
  data: ListOrganizationApiKeysResponseData[];
  list_metadata: ListOrganizationApiKeysResponseListMetadata;
}

export interface CreateOrganizationApiKeyRequest {
  name: string;
  permissions: string[];
}

export interface CreateOrganizationApiKeyResponse {
  id: string;
  value: string;
  obfuscatedValue: string;
  createdAt: string;
  name: string;
  permissions: string[];
}

export interface ListOrganizationApiKeyPermission {
  id: string;
  slug: string;
  name: string;
  /** @nullable */
  description?: string | null;
}

export type ListOrganizationApiKeyPermissionsResponseListMetadata = {
  before?: string;
  after?: string;
};

export interface ListOrganizationApiKeyPermissionsResponse {
  data: ListOrganizationApiKeyPermission[];
  list_metadata: ListOrganizationApiKeyPermissionsResponseListMetadata;
}

export type ListUserApiKeysResponseDataOwner = {
  type: "user";
  id: string;
  email: string;
  /** @nullable */
  firstName?: string | null;
  /** @nullable */
  lastName?: string | null;
  displayName: string;
};

export interface ListUserApiKeysResponseData {
  id: string;
  owner: ListUserApiKeysResponseDataOwner;
  name: string;
  obfuscatedValue: string;
  createdAt: string;
  /** @nullable */
  lastUsedAt?: string | null;
  permissions: string[];
}

export type ListUserApiKeysResponseListMetadata = {
  before?: string;
  after?: string;
};

export interface ListUserApiKeysResponse {
  data: ListUserApiKeysResponseData[];
  list_metadata: ListUserApiKeysResponseListMetadata;
}

export interface CreateUserApiKeyRequest {
  name: string;
  permissions: string[];
}

export interface CreateUserApiKeyResponse {
  id: string;
  value: string;
  obfuscatedValue: string;
  createdAt: string;
  name: string;
  permissions: string[];
}

export interface ListUserApiKeyPermission {
  id: string;
  slug: string;
  name: string;
  /** @nullable */
  description?: string | null;
}

export type ListUserApiKeyPermissionsResponseListMetadata = {
  before?: string;
  after?: string;
};

export interface ListUserApiKeyPermissionsResponse {
  data: ListUserApiKeyPermission[];
  list_metadata: ListUserApiKeyPermissionsResponseListMetadata;
}

export type DataInstallation = {
  object: "data_installation" | "connected_account";
  id: string;
  /** @nullable */
  user_id?: string | null;
  /** @nullable */
  organization_id?: string | null;
  scopes: string[];
  state: "connected" | "needs_reauthorization";
  created_at: string;
  updated_at: string;
} & {
  /** @nullable */
  userlandUserId?: string | null;
  /** @nullable */
  organizationId?: string | null;
  createdAt?: string;
  updatedAt?: string;
};

export type DataIntegration = {
  object: "data_integration";
  id: string;
  name: string;
  /** @nullable */
  description?: string | null;
  slug: string;
  integrationType:
    | "asana"
    | "attio"
    | "box"
    | "cal-dot-com"
    | "calendly"
    | "confluence"
    | "datadog"
    | "dropbox"
    | "frame-io"
    | "front"
    | "github"
    | "gitlab"
    | "gmail"
    | "gong"
    | "google"
    | "google-calendar"
    | "google-drive"
    | "helpscout"
    | "hubspot"
    | "intercom"
    | "intralinks"
    | "jira"
    | "linear"
    | "microsoft"
    | "microsoft-onedrive"
    | "microsoft-onenote"
    | "microsoft-outlook"
    | "microsoft-outlook-calendar"
    | "microsoft-sharepoint"
    | "microsoft-teams"
    | "microsoft-todo"
    | "notion"
    | "prefect"
    | "pydantic-logfire"
    | "quickbooks"
    | "salesforce"
    | "sentry"
    | "slack"
    | "snowflake"
    | "stripe"
    | "xero"
    | "zendesk"
    | "zoom"
    | string;
  ownership: "userland_user" | "organization";
  credentialsType: "shared" | "custom";
  scopes?: string[] | null;
  createdAt: string;
  updatedAt: string;
  installation?: DataInstallation | null;
} & {
  iconSlug?: string;
  iconUrl?: string;
  iconDarkUrl?: string;
};

export interface DataIntegrationsResponse {
  data: DataIntegration[];
}

export type GetDataIntegrationAuthorizeUrlResponse = {
  url: string;
  redirectToken: string;
} & {
  handoffToken?: string;
};

export interface GetAuthorizationStatusResponse {
  isConnecting: boolean;
}

export type DirectoryType = (typeof DirectoryType)[keyof typeof DirectoryType];

export const DirectoryType = {
  azure_scim_v20: "azure scim v2.0",
  bamboohr: "bamboohr",
  breathe_hr: "breathe hr",
  cezanne_hr: "cezanne hr",
  cyberark_scim_v20: "cyberark scim v2.0",
  fourth_hr: "fourth hr",
  generic_scim_v20: "generic scim v2.0",
  gsuite_directory: "gsuite directory",
  gusto: "gusto",
  hibob: "hibob",
  jump_cloud_scim_v20: "jump cloud scim v2.0",
  okta_scim_v20: "okta scim v2.0",
  onelogin_scim_v20: "onelogin scim v2.0",
  people_hr: "people hr",
  personio: "personio",
  pingfederate_scim_v20: "pingfederate scim v2.0",
  rippling: "rippling",
  rippling_scim_v20: "rippling scim v2.0",
  s3: "s3",
  sailpoint_scim_v20: "sailpoint scim v2.0",
  sftp: "sftp",
  sftp_workday: "sftp workday",
  workday: "workday",
} as const;

export type DirectoryState =
  (typeof DirectoryState)[keyof typeof DirectoryState];

export const DirectoryState = {
  requires_type: "requires_type",
  linked: "linked",
  validating: "validating",
  invalid_credentials: "invalid_credentials",
  unlinked: "unlinked",
  deleting: "deleting",
} as const;

export interface DirectoryUsersMetadata {
  active: number;
  inactive: number;
}

export interface DirectoryMetadata {
  users: DirectoryUsersMetadata;
  groups: number;
}

export interface Directory {
  id: string;
  name: string;
  type: DirectoryType;
  state: DirectoryState;
  createdAt: string;
  updatedAt: string;
  /** @nullable */
  lastSyncRunAt?: string | null;
  metadata: DirectoryMetadata;
}

export interface DirectoriesResponse {
  data: Directory[];
  link: string;
}

export type CreateOrganizationApiKey400ErrorsItem = {
  code: string;
  field: string;
};

export type CreateOrganizationApiKey400 = {
  message: string;
  errors: CreateOrganizationApiKey400ErrorsItem[];
};

export type CreateOrganizationApiKey403 = {
  message: string;
};

export type CreateOrganizationApiKey404 = {
  message: string;
};

export type CreateOrganizationApiKey422ErrorsItem = {
  code: string;
  field: string;
};

export type CreateOrganizationApiKey422 = {
  message: string;
  errors: CreateOrganizationApiKey422ErrorsItem[];
};

export type ListOrganizationApiKeysParams = {
  limit?: number;
  before?: string;
  after?: string;
  search?: string;
};

export type ListOrganizationApiKeys400ErrorsItem = {
  code: string;
  field: string;
};

export type ListOrganizationApiKeys400 = {
  message: string;
  errors: ListOrganizationApiKeys400ErrorsItem[];
};

export type ListOrganizationApiKeys403 = {
  message: string;
};

export type ListOrganizationApiKeyPermissionsParams = {
  search?: string;
  limit?: number;
  before?: string;
  after?: string;
};

export type ListOrganizationApiKeyPermissions400ErrorsItem = {
  code: string;
  field: string;
};

export type ListOrganizationApiKeyPermissions400 = {
  message: string;
  errors: ListOrganizationApiKeyPermissions400ErrorsItem[];
};

export type ListOrganizationApiKeyPermissions403 = {
  message: string;
};

export type DeleteOrganizationApiKey200 = {
  success: boolean;
};

export type DeleteOrganizationApiKey403 = {
  message: string;
};

export type DeleteOrganizationApiKey404 = {
  message: string;
};

export type DeleteDataInstallation200 = {
  success: boolean;
};

export type DeleteDataInstallation403 = {
  message: string;
};

export type DeleteDataInstallation404 = {
  message: string;
};

export type MyDataIntegrations403 = {
  message: string;
};

export type GetDataInstallationAuthorizationStatus400 = {
  message: string;
};

export type GetDataInstallationAuthorizationStatus403 = {
  message: string;
};

export type GetDataInstallationAuthorizationStatus404 = {
  message: string;
};

export type GetDataIntegrationAuthorizeUrlParams = {
  requireHandoff?: boolean;
};

export type GetDataIntegrationAuthorizeUrl400 = {
  message: string;
};

export type GetDataIntegrationAuthorizeUrl403 = {
  message: string;
};

export type GetDataIntegrationAuthorizeUrl404 = {
  message: string;
};

export type CreateUserApiKey400ErrorsItem = {
  code: string;
  field: string;
};

export type CreateUserApiKey400 = {
  message: string;
  errors: CreateUserApiKey400ErrorsItem[];
};

export type CreateUserApiKey403 = {
  message: string;
};

export type CreateUserApiKey404 = {
  message: string;
};

export type CreateUserApiKey422ErrorsItem = {
  code: string;
  field: string;
};

export type CreateUserApiKey422 = {
  message: string;
  errors: CreateUserApiKey422ErrorsItem[];
};

export type ListUserApiKeysParams = {
  limit?: number;
  before?: string;
  after?: string;
  search?: string;
};

export type ListUserApiKeys400ErrorsItem = {
  code: string;
  field: string;
};

export type ListUserApiKeys400 = {
  message: string;
  errors: ListUserApiKeys400ErrorsItem[];
};

export type ListUserApiKeys403 = {
  message: string;
};

export type ListUserApiKeyPermissionsParams = {
  search?: string;
  limit?: number;
  before?: string;
  after?: string;
};

export type ListUserApiKeyPermissions400ErrorsItem = {
  code: string;
  field: string;
};

export type ListUserApiKeyPermissions400 = {
  message: string;
  errors: ListUserApiKeyPermissions400ErrorsItem[];
};

export type ListUserApiKeyPermissions403 = {
  message: string;
};

export type DeleteUserApiKey200 = {
  success: boolean;
};

export type DeleteUserApiKey403 = {
  message: string;
};

export type DeleteUserApiKey404 = {
  message: string;
};

export type InviteMemberBody = {
  email: string;
  /** @nullable */
  firstName?: string | null;
  /** @nullable */
  lastName?: string | null;
  roles: string[];
};

export type InviteMember400 = {
  message: string;
};

export type InviteMember403 = {
  message: string;
};

export type InviteMember404 = {
  message: string;
};

export type RevokeInvite400 = {
  message: string;
};

export type RevokeInvite403 = {
  message: string;
};

export type RevokeInvite404 = {
  message: string;
};

export type ResendInvite400 = {
  message: string;
};

export type ResendInvite403 = {
  message: string;
};

export type ResendInvite404 = {
  message: string;
};

export type ResendInvite422 = {
  message: string;
};

export type MembersParams = {
  search?: string;
  limit?: string;
  before?: string;
  after?: string;
  role?: string;
};

export type Members403 = {
  message: string;
};

export type Members404 = {
  message: string;
};

export type RemoveMember400 = {
  message: string;
};

export type RemoveMember403 = {
  message: string;
};

export type RemoveMember404 = {
  message: string;
};

export type UpdateMemberBody = {
  roles: string[];
};

export type UpdateMember400 = {
  message: string;
};

export type UpdateMember403 = {
  message: string;
};

export type UpdateMember404 = {
  message: string;
};

export type UpdateMember422 = {
  message: string;
};

export type Organizations403 = {
  message: string;
};

export type Organizations404 = {
  message: string;
};

export type Roles403 = {
  message: string;
};

export type Roles404 = {
  message: string;
};

export type RolesAndConfig403 = {
  message: string;
};

export type RolesAndConfig404 = {
  message: string;
};

export type AuthenticationInformation403 = {
  message: string;
};

export type CreatePassword201 = {
  success: boolean;
};

export type CreatePassword400 = {
  message: string;
};

export type CreatePassword403 = {
  message: string;
};

export type CreateTotpFactor400 = {
  message: string;
};

export type CreateTotpFactor403 = {
  message: string;
};

export type Me403 = {
  message: string;
};

export type UpdateMeBody = {
  firstName?: string;
  lastName?: string;
  locale?: string;
};

export type UpdateMe400 = {
  message: string;
};

export type UpdateMe403 = {
  message: string;
};

export type RegisterPasskey400 = {
  message: string;
};

export type RegisterPasskey403 = {
  message: string;
};

export type VerifyPasskey200 = {
  success: boolean;
};

export type VerifyPasskey400 = {
  message: string;
};

export type VerifyPasskey403 = {
  message: string;
};

export type DeletePasskey200 = {
  success: boolean;
};

export type DeletePasskey400 = {
  message: string;
};

export type DeletePasskey403 = {
  message: string;
};

export type SendEmailChange400 = {
  message: string;
};

export type SendEmailChange403 = {
  message: string;
};

export type SendEmailChange409 = {
  message: string;
};

export type SendEmailChange422 = {
  message: string;
};

export type SendEmailChange429 = {
  message: string;
};

export type SendVerification400 = {
  message: string;
};

export type SendVerification403 = {
  message: string;
};

export type Sessions403 = {
  message: string;
};

export type RevokeAllSessions200 = {
  success: boolean;
};

export type RevokeAllSessions400 = {
  message: string;
};

export type RevokeAllSessions403 = {
  message: string;
};

export type RevokeSession200 = {
  success: boolean;
};

export type RevokeSession400 = {
  message: string;
};

export type RevokeSession403 = {
  message: string;
};

export type DeleteTotpFactors200 = {
  success: boolean;
};

export type DeleteTotpFactors400 = {
  message: string;
};

export type DeleteTotpFactors403 = {
  message: string;
};

export type UpdatePassword201 = {
  success: boolean;
};

export type UpdatePassword400 = {
  message: string;
};

export type UpdatePassword403 = {
  message: string;
};

export type Verify400 = {
  message: string;
};

export type Verify403 = {
  message: string;
};

export type VerifyEmailChange400 = {
  message: string;
};

export type VerifyEmailChange403 = {
  message: string;
};

export type VerifyEmailChange409 = {
  message: string;
};

export type VerifyEmailChange422 = {
  message: string;
};

export type VerifyEmailChange429 = {
  message: string;
};

export type VerifyTotpFactor200 = {
  success: boolean;
};

export type VerifyTotpFactor400 = {
  message: string;
};

export type VerifyTotpFactor403 = {
  message: string;
};

export type GetAuditLogStream200 =
  | AuditLogStreamJSON
  | { [key: string]: unknown };

export type GetAuditLogStream403 = {
  message: string;
};

export type GenerateAdminPortalLinkParams = {
  intent: GenerateAdminPortalLinkIntent;
};

export type GenerateAdminPortalLinkIntent =
  (typeof GenerateAdminPortalLinkIntent)[keyof typeof GenerateAdminPortalLinkIntent];

export const GenerateAdminPortalLinkIntent = {
  domain_verification: "domain_verification",
  sso: "sso",
  log_streams: "log_streams",
} as const;

export type GenerateAdminPortalLink201 = {
  link: string;
};

export type GenerateAdminPortalLink403 = {
  message: string;
};

export type GenerateAdminPortalLink404 = {
  message: string;
};

export type GenerateAdminPortalLink500 = {
  message: string;
};

export type ListOrganizationDomains200 = {
  data: OrganizationDomain[];
};

export type ListOrganizationDomains403 = {
  message: string;
};

export type ListOrganizationDomains404 = {
  message: string;
};

export type DeleteOrganizationDomain403 = {
  message: string;
};

export type DeleteOrganizationDomain404 = {
  message: string;
};

export type ReverifyOrganizationDomain403 = {
  message: string;
};

export type ReverifyOrganizationDomain404 = {
  message: string;
};

export type ListDirectories403 = {
  message: string;
};

export type ListDirectories404 = {
  message: string;
};

export type GetDirectory403 = {
  message: string;
};

export type GetDirectory404 = {
  message: string;
};

export type Settings403 = {
  message: string;
};

export type createOrganizationApiKeyResponse201 = {
  data: CreateOrganizationApiKeyResponse;
  status: 201;
};

export type createOrganizationApiKeyResponse400 = {
  data: CreateOrganizationApiKey400;
  status: 400;
};

export type createOrganizationApiKeyResponse403 = {
  data: CreateOrganizationApiKey403;
  status: 403;
};

export type createOrganizationApiKeyResponse404 = {
  data: CreateOrganizationApiKey404;
  status: 404;
};

export type createOrganizationApiKeyResponse422 = {
  data: CreateOrganizationApiKey422;
  status: 422;
};

export type createOrganizationApiKeyResponseSuccess =
  createOrganizationApiKeyResponse201 & {
    headers: Headers;
  };
export type createOrganizationApiKeyResponseError = (
  | createOrganizationApiKeyResponse400
  | createOrganizationApiKeyResponse403
  | createOrganizationApiKeyResponse404
  | createOrganizationApiKeyResponse422
) & {
  headers: Headers;
};

export type createOrganizationApiKeyResponse =
  | createOrganizationApiKeyResponseSuccess
  | createOrganizationApiKeyResponseError;

export const getCreateOrganizationApiKeyUrl = () => {
  return `https://api.workos.com/_widgets/ApiKeys/organization-api-keys`;
};

export const createOrganizationApiKey = async (
  createOrganizationApiKeyRequest: CreateOrganizationApiKeyRequest,
  options?: RequestInit,
): Promise<createOrganizationApiKeyResponse> => {
  const res = await fetch(getCreateOrganizationApiKeyUrl(), {
    ...options,
    method: "POST",
    headers: { "Content-Type": "application/json", ...options?.headers },
    body: JSON.stringify(createOrganizationApiKeyRequest),
  });

  const body = [204, 205, 304].includes(res.status) ? null : await res.text();

  const data: createOrganizationApiKeyResponse["data"] = body
    ? JSON.parse(body)
    : {};
  return {
    data,
    status: res.status,
    headers: res.headers,
  } as createOrganizationApiKeyResponse;
};

export const getCreateOrganizationApiKeyMutationOptions = <
  TError =
    | CreateOrganizationApiKey400
    | CreateOrganizationApiKey403
    | CreateOrganizationApiKey404
    | CreateOrganizationApiKey422,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof createOrganizationApiKey>>,
    TError,
    { data: CreateOrganizationApiKeyRequest },
    TContext
  >;
  fetch?: RequestInit;
}): UseMutationOptions<
  Awaited<ReturnType<typeof createOrganizationApiKey>>,
  TError,
  { data: CreateOrganizationApiKeyRequest },
  TContext
> => {
  const mutationKey = ["createOrganizationApiKey"];
  const { mutation: mutationOptions, fetch: fetchOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, fetch: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof createOrganizationApiKey>>,
    { data: CreateOrganizationApiKeyRequest }
  > = (props) => {
    const { data } = props ?? {};

    return createOrganizationApiKey(data, fetchOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type CreateOrganizationApiKeyMutationResult = NonNullable<
  Awaited<ReturnType<typeof createOrganizationApiKey>>
>;
export type CreateOrganizationApiKeyMutationBody =
  CreateOrganizationApiKeyRequest;
export type CreateOrganizationApiKeyMutationError =
  | CreateOrganizationApiKey400
  | CreateOrganizationApiKey403
  | CreateOrganizationApiKey404
  | CreateOrganizationApiKey422;

export const useCreateOrganizationApiKey = <
  TError =
    | CreateOrganizationApiKey400
    | CreateOrganizationApiKey403
    | CreateOrganizationApiKey404
    | CreateOrganizationApiKey422,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof createOrganizationApiKey>>,
      TError,
      { data: CreateOrganizationApiKeyRequest },
      TContext
    >;
    fetch?: RequestInit;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof createOrganizationApiKey>>,
  TError,
  { data: CreateOrganizationApiKeyRequest },
  TContext
> => {
  return useMutation(
    getCreateOrganizationApiKeyMutationOptions(options),
    queryClient,
  );
};

export type listOrganizationApiKeysResponse200 = {
  data: ListOrganizationApiKeysResponse;
  status: 200;
};

export type listOrganizationApiKeysResponse400 = {
  data: ListOrganizationApiKeys400;
  status: 400;
};

export type listOrganizationApiKeysResponse403 = {
  data: ListOrganizationApiKeys403;
  status: 403;
};

export type listOrganizationApiKeysResponseSuccess =
  listOrganizationApiKeysResponse200 & {
    headers: Headers;
  };
export type listOrganizationApiKeysResponseError = (
  | listOrganizationApiKeysResponse400
  | listOrganizationApiKeysResponse403
) & {
  headers: Headers;
};

export type listOrganizationApiKeysResponse =
  | listOrganizationApiKeysResponseSuccess
  | listOrganizationApiKeysResponseError;

export const getListOrganizationApiKeysUrl = (
  params?: ListOrganizationApiKeysParams,
) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== undefined) {
      normalizedParams.append(key, value === null ? "null" : value.toString());
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0
    ? `https://api.workos.com/_widgets/ApiKeys/organization-api-keys?${stringifiedParams}`
    : `https://api.workos.com/_widgets/ApiKeys/organization-api-keys`;
};

export const listOrganizationApiKeys = async (
  params?: ListOrganizationApiKeysParams,
  options?: RequestInit,
): Promise<listOrganizationApiKeysResponse> => {
  const res = await fetch(getListOrganizationApiKeysUrl(params), {
    ...options,
    method: "GET",
  });

  const body = [204, 205, 304].includes(res.status) ? null : await res.text();

  const data: listOrganizationApiKeysResponse["data"] = body
    ? JSON.parse(body)
    : {};
  return {
    data,
    status: res.status,
    headers: res.headers,
  } as listOrganizationApiKeysResponse;
};

export const getListOrganizationApiKeysQueryKey = (
  params?: ListOrganizationApiKeysParams,
) => {
  return [
    `https://api.workos.com/_widgets/ApiKeys/organization-api-keys`,
    ...(params ? [params] : []),
  ] as const;
};

export const getListOrganizationApiKeysQueryOptions = <
  TData = Awaited<ReturnType<typeof listOrganizationApiKeys>>,
  TError = ListOrganizationApiKeys400 | ListOrganizationApiKeys403,
>(
  params?: ListOrganizationApiKeysParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof listOrganizationApiKeys>>,
        TError,
        TData
      >
    >;
    fetch?: RequestInit;
  },
) => {
  const { query: queryOptions, fetch: fetchOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getListOrganizationApiKeysQueryKey(params);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof listOrganizationApiKeys>>
  > = ({ signal }) =>
    listOrganizationApiKeys(params, { signal, ...fetchOptions });

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof listOrganizationApiKeys>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type ListOrganizationApiKeysQueryResult = NonNullable<
  Awaited<ReturnType<typeof listOrganizationApiKeys>>
>;
export type ListOrganizationApiKeysQueryError =
  | ListOrganizationApiKeys400
  | ListOrganizationApiKeys403;

export function useListOrganizationApiKeys<
  TData = Awaited<ReturnType<typeof listOrganizationApiKeys>>,
  TError = ListOrganizationApiKeys400 | ListOrganizationApiKeys403,
>(
  params: undefined | ListOrganizationApiKeysParams,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof listOrganizationApiKeys>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listOrganizationApiKeys>>,
          TError,
          Awaited<ReturnType<typeof listOrganizationApiKeys>>
        >,
        "initialData"
      >;
    fetch?: RequestInit;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useListOrganizationApiKeys<
  TData = Awaited<ReturnType<typeof listOrganizationApiKeys>>,
  TError = ListOrganizationApiKeys400 | ListOrganizationApiKeys403,
>(
  params?: ListOrganizationApiKeysParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof listOrganizationApiKeys>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listOrganizationApiKeys>>,
          TError,
          Awaited<ReturnType<typeof listOrganizationApiKeys>>
        >,
        "initialData"
      >;
    fetch?: RequestInit;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useListOrganizationApiKeys<
  TData = Awaited<ReturnType<typeof listOrganizationApiKeys>>,
  TError = ListOrganizationApiKeys400 | ListOrganizationApiKeys403,
>(
  params?: ListOrganizationApiKeysParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof listOrganizationApiKeys>>,
        TError,
        TData
      >
    >;
    fetch?: RequestInit;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};

export function useListOrganizationApiKeys<
  TData = Awaited<ReturnType<typeof listOrganizationApiKeys>>,
  TError = ListOrganizationApiKeys400 | ListOrganizationApiKeys403,
>(
  params?: ListOrganizationApiKeysParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof listOrganizationApiKeys>>,
        TError,
        TData
      >
    >;
    fetch?: RequestInit;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions = getListOrganizationApiKeysQueryOptions(params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData, TError> };

  return { ...query, queryKey: queryOptions.queryKey };
}

export type listOrganizationApiKeyPermissionsResponse200 = {
  data: ListOrganizationApiKeyPermissionsResponse;
  status: 200;
};

export type listOrganizationApiKeyPermissionsResponse400 = {
  data: ListOrganizationApiKeyPermissions400;
  status: 400;
};

export type listOrganizationApiKeyPermissionsResponse403 = {
  data: ListOrganizationApiKeyPermissions403;
  status: 403;
};

export type listOrganizationApiKeyPermissionsResponseSuccess =
  listOrganizationApiKeyPermissionsResponse200 & {
    headers: Headers;
  };
export type listOrganizationApiKeyPermissionsResponseError = (
  | listOrganizationApiKeyPermissionsResponse400
  | listOrganizationApiKeyPermissionsResponse403
) & {
  headers: Headers;
};

export type listOrganizationApiKeyPermissionsResponse =
  | listOrganizationApiKeyPermissionsResponseSuccess
  | listOrganizationApiKeyPermissionsResponseError;

export const getListOrganizationApiKeyPermissionsUrl = (
  params?: ListOrganizationApiKeyPermissionsParams,
) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== undefined) {
      normalizedParams.append(key, value === null ? "null" : value.toString());
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0
    ? `https://api.workos.com/_widgets/ApiKeys/permissions?${stringifiedParams}`
    : `https://api.workos.com/_widgets/ApiKeys/permissions`;
};

export const listOrganizationApiKeyPermissions = async (
  params?: ListOrganizationApiKeyPermissionsParams,
  options?: RequestInit,
): Promise<listOrganizationApiKeyPermissionsResponse> => {
  const res = await fetch(getListOrganizationApiKeyPermissionsUrl(params), {
    ...options,
    method: "GET",
  });

  const body = [204, 205, 304].includes(res.status) ? null : await res.text();

  const data: listOrganizationApiKeyPermissionsResponse["data"] = body
    ? JSON.parse(body)
    : {};
  return {
    data,
    status: res.status,
    headers: res.headers,
  } as listOrganizationApiKeyPermissionsResponse;
};

export const getListOrganizationApiKeyPermissionsQueryKey = (
  params?: ListOrganizationApiKeyPermissionsParams,
) => {
  return [
    `https://api.workos.com/_widgets/ApiKeys/permissions`,
    ...(params ? [params] : []),
  ] as const;
};

export const getListOrganizationApiKeyPermissionsQueryOptions = <
  TData = Awaited<ReturnType<typeof listOrganizationApiKeyPermissions>>,
  TError =
    | ListOrganizationApiKeyPermissions400
    | ListOrganizationApiKeyPermissions403,
>(
  params?: ListOrganizationApiKeyPermissionsParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof listOrganizationApiKeyPermissions>>,
        TError,
        TData
      >
    >;
    fetch?: RequestInit;
  },
) => {
  const { query: queryOptions, fetch: fetchOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getListOrganizationApiKeyPermissionsQueryKey(params);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof listOrganizationApiKeyPermissions>>
  > = ({ signal }) =>
    listOrganizationApiKeyPermissions(params, { signal, ...fetchOptions });

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof listOrganizationApiKeyPermissions>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type ListOrganizationApiKeyPermissionsQueryResult = NonNullable<
  Awaited<ReturnType<typeof listOrganizationApiKeyPermissions>>
>;
export type ListOrganizationApiKeyPermissionsQueryError =
  | ListOrganizationApiKeyPermissions400
  | ListOrganizationApiKeyPermissions403;

export function useListOrganizationApiKeyPermissions<
  TData = Awaited<ReturnType<typeof listOrganizationApiKeyPermissions>>,
  TError =
    | ListOrganizationApiKeyPermissions400
    | ListOrganizationApiKeyPermissions403,
>(
  params: undefined | ListOrganizationApiKeyPermissionsParams,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof listOrganizationApiKeyPermissions>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listOrganizationApiKeyPermissions>>,
          TError,
          Awaited<ReturnType<typeof listOrganizationApiKeyPermissions>>
        >,
        "initialData"
      >;
    fetch?: RequestInit;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useListOrganizationApiKeyPermissions<
  TData = Awaited<ReturnType<typeof listOrganizationApiKeyPermissions>>,
  TError =
    | ListOrganizationApiKeyPermissions400
    | ListOrganizationApiKeyPermissions403,
>(
  params?: ListOrganizationApiKeyPermissionsParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof listOrganizationApiKeyPermissions>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listOrganizationApiKeyPermissions>>,
          TError,
          Awaited<ReturnType<typeof listOrganizationApiKeyPermissions>>
        >,
        "initialData"
      >;
    fetch?: RequestInit;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useListOrganizationApiKeyPermissions<
  TData = Awaited<ReturnType<typeof listOrganizationApiKeyPermissions>>,
  TError =
    | ListOrganizationApiKeyPermissions400
    | ListOrganizationApiKeyPermissions403,
>(
  params?: ListOrganizationApiKeyPermissionsParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof listOrganizationApiKeyPermissions>>,
        TError,
        TData
      >
    >;
    fetch?: RequestInit;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};

export function useListOrganizationApiKeyPermissions<
  TData = Awaited<ReturnType<typeof listOrganizationApiKeyPermissions>>,
  TError =
    | ListOrganizationApiKeyPermissions400
    | ListOrganizationApiKeyPermissions403,
>(
  params?: ListOrganizationApiKeyPermissionsParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof listOrganizationApiKeyPermissions>>,
        TError,
        TData
      >
    >;
    fetch?: RequestInit;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions = getListOrganizationApiKeyPermissionsQueryOptions(
    params,
    options,
  );

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData, TError> };

  return { ...query, queryKey: queryOptions.queryKey };
}

export type deleteOrganizationApiKeyResponse200 = {
  data: DeleteOrganizationApiKey200;
  status: 200;
};

export type deleteOrganizationApiKeyResponse403 = {
  data: DeleteOrganizationApiKey403;
  status: 403;
};

export type deleteOrganizationApiKeyResponse404 = {
  data: DeleteOrganizationApiKey404;
  status: 404;
};

export type deleteOrganizationApiKeyResponseSuccess =
  deleteOrganizationApiKeyResponse200 & {
    headers: Headers;
  };
export type deleteOrganizationApiKeyResponseError = (
  | deleteOrganizationApiKeyResponse403
  | deleteOrganizationApiKeyResponse404
) & {
  headers: Headers;
};

export type deleteOrganizationApiKeyResponse =
  | deleteOrganizationApiKeyResponseSuccess
  | deleteOrganizationApiKeyResponseError;

export const getDeleteOrganizationApiKeyUrl = (apiKeyId: string) => {
  return `https://api.workos.com/_widgets/ApiKeys/${apiKeyId}`;
};

export const deleteOrganizationApiKey = async (
  apiKeyId: string,
  options?: RequestInit,
): Promise<deleteOrganizationApiKeyResponse> => {
  const res = await fetch(getDeleteOrganizationApiKeyUrl(apiKeyId), {
    ...options,
    method: "DELETE",
  });

  const body = [204, 205, 304].includes(res.status) ? null : await res.text();

  const data: deleteOrganizationApiKeyResponse["data"] = body
    ? JSON.parse(body)
    : {};
  return {
    data,
    status: res.status,
    headers: res.headers,
  } as deleteOrganizationApiKeyResponse;
};

export const getDeleteOrganizationApiKeyMutationOptions = <
  TError = DeleteOrganizationApiKey403 | DeleteOrganizationApiKey404,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof deleteOrganizationApiKey>>,
    TError,
    { apiKeyId: string },
    TContext
  >;
  fetch?: RequestInit;
}): UseMutationOptions<
  Awaited<ReturnType<typeof deleteOrganizationApiKey>>,
  TError,
  { apiKeyId: string },
  TContext
> => {
  const mutationKey = ["deleteOrganizationApiKey"];
  const { mutation: mutationOptions, fetch: fetchOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, fetch: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof deleteOrganizationApiKey>>,
    { apiKeyId: string }
  > = (props) => {
    const { apiKeyId } = props ?? {};

    return deleteOrganizationApiKey(apiKeyId, fetchOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type DeleteOrganizationApiKeyMutationResult = NonNullable<
  Awaited<ReturnType<typeof deleteOrganizationApiKey>>
>;

export type DeleteOrganizationApiKeyMutationError =
  | DeleteOrganizationApiKey403
  | DeleteOrganizationApiKey404;

export const useDeleteOrganizationApiKey = <
  TError = DeleteOrganizationApiKey403 | DeleteOrganizationApiKey404,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof deleteOrganizationApiKey>>,
      TError,
      { apiKeyId: string },
      TContext
    >;
    fetch?: RequestInit;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof deleteOrganizationApiKey>>,
  TError,
  { apiKeyId: string },
  TContext
> => {
  return useMutation(
    getDeleteOrganizationApiKeyMutationOptions(options),
    queryClient,
  );
};

export type deleteDataInstallationResponse200 = {
  data: DeleteDataInstallation200;
  status: 200;
};

export type deleteDataInstallationResponse403 = {
  data: DeleteDataInstallation403;
  status: 403;
};

export type deleteDataInstallationResponse404 = {
  data: DeleteDataInstallation404;
  status: 404;
};

export type deleteDataInstallationResponseSuccess =
  deleteDataInstallationResponse200 & {
    headers: Headers;
  };
export type deleteDataInstallationResponseError = (
  | deleteDataInstallationResponse403
  | deleteDataInstallationResponse404
) & {
  headers: Headers;
};

export type deleteDataInstallationResponse =
  | deleteDataInstallationResponseSuccess
  | deleteDataInstallationResponseError;

export const getDeleteDataInstallationUrl = (installationId: string) => {
  return `https://api.workos.com/_widgets/DataIntegrations/installations/${installationId}`;
};

export const deleteDataInstallation = async (
  installationId: string,
  options?: RequestInit,
): Promise<deleteDataInstallationResponse> => {
  const res = await fetch(getDeleteDataInstallationUrl(installationId), {
    ...options,
    method: "DELETE",
  });

  const body = [204, 205, 304].includes(res.status) ? null : await res.text();

  const data: deleteDataInstallationResponse["data"] = body
    ? JSON.parse(body)
    : {};
  return {
    data,
    status: res.status,
    headers: res.headers,
  } as deleteDataInstallationResponse;
};

export const getDeleteDataInstallationMutationOptions = <
  TError = DeleteDataInstallation403 | DeleteDataInstallation404,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof deleteDataInstallation>>,
    TError,
    { installationId: string },
    TContext
  >;
  fetch?: RequestInit;
}): UseMutationOptions<
  Awaited<ReturnType<typeof deleteDataInstallation>>,
  TError,
  { installationId: string },
  TContext
> => {
  const mutationKey = ["deleteDataInstallation"];
  const { mutation: mutationOptions, fetch: fetchOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, fetch: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof deleteDataInstallation>>,
    { installationId: string }
  > = (props) => {
    const { installationId } = props ?? {};

    return deleteDataInstallation(installationId, fetchOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type DeleteDataInstallationMutationResult = NonNullable<
  Awaited<ReturnType<typeof deleteDataInstallation>>
>;

export type DeleteDataInstallationMutationError =
  | DeleteDataInstallation403
  | DeleteDataInstallation404;

export const useDeleteDataInstallation = <
  TError = DeleteDataInstallation403 | DeleteDataInstallation404,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof deleteDataInstallation>>,
      TError,
      { installationId: string },
      TContext
    >;
    fetch?: RequestInit;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof deleteDataInstallation>>,
  TError,
  { installationId: string },
  TContext
> => {
  return useMutation(
    getDeleteDataInstallationMutationOptions(options),
    queryClient,
  );
};

export type myDataIntegrationsResponse200 = {
  data: DataIntegrationsResponse;
  status: 200;
};

export type myDataIntegrationsResponse403 = {
  data: MyDataIntegrations403;
  status: 403;
};

export type myDataIntegrationsResponseSuccess =
  myDataIntegrationsResponse200 & {
    headers: Headers;
  };
export type myDataIntegrationsResponseError = myDataIntegrationsResponse403 & {
  headers: Headers;
};

export type myDataIntegrationsResponse =
  | myDataIntegrationsResponseSuccess
  | myDataIntegrationsResponseError;

export const getMyDataIntegrationsUrl = () => {
  return `https://api.workos.com/_widgets/DataIntegrations/mine`;
};

export const myDataIntegrations = async (
  options?: RequestInit,
): Promise<myDataIntegrationsResponse> => {
  const res = await fetch(getMyDataIntegrationsUrl(), {
    ...options,
    method: "GET",
  });

  const body = [204, 205, 304].includes(res.status) ? null : await res.text();

  const data: myDataIntegrationsResponse["data"] = body ? JSON.parse(body) : {};
  return {
    data,
    status: res.status,
    headers: res.headers,
  } as myDataIntegrationsResponse;
};

export const getMyDataIntegrationsQueryKey = () => {
  return [`https://api.workos.com/_widgets/DataIntegrations/mine`] as const;
};

export const getMyDataIntegrationsQueryOptions = <
  TData = Awaited<ReturnType<typeof myDataIntegrations>>,
  TError = MyDataIntegrations403,
>(options?: {
  query?: Partial<
    UseQueryOptions<
      Awaited<ReturnType<typeof myDataIntegrations>>,
      TError,
      TData
    >
  >;
  fetch?: RequestInit;
}) => {
  const { query: queryOptions, fetch: fetchOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getMyDataIntegrationsQueryKey();

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof myDataIntegrations>>
  > = ({ signal }) => myDataIntegrations({ signal, ...fetchOptions });

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof myDataIntegrations>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type MyDataIntegrationsQueryResult = NonNullable<
  Awaited<ReturnType<typeof myDataIntegrations>>
>;
export type MyDataIntegrationsQueryError = MyDataIntegrations403;

export function useMyDataIntegrations<
  TData = Awaited<ReturnType<typeof myDataIntegrations>>,
  TError = MyDataIntegrations403,
>(
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof myDataIntegrations>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof myDataIntegrations>>,
          TError,
          Awaited<ReturnType<typeof myDataIntegrations>>
        >,
        "initialData"
      >;
    fetch?: RequestInit;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useMyDataIntegrations<
  TData = Awaited<ReturnType<typeof myDataIntegrations>>,
  TError = MyDataIntegrations403,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof myDataIntegrations>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof myDataIntegrations>>,
          TError,
          Awaited<ReturnType<typeof myDataIntegrations>>
        >,
        "initialData"
      >;
    fetch?: RequestInit;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useMyDataIntegrations<
  TData = Awaited<ReturnType<typeof myDataIntegrations>>,
  TError = MyDataIntegrations403,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof myDataIntegrations>>,
        TError,
        TData
      >
    >;
    fetch?: RequestInit;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};

export function useMyDataIntegrations<
  TData = Awaited<ReturnType<typeof myDataIntegrations>>,
  TError = MyDataIntegrations403,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof myDataIntegrations>>,
        TError,
        TData
      >
    >;
    fetch?: RequestInit;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions = getMyDataIntegrationsQueryOptions(options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData, TError> };

  return { ...query, queryKey: queryOptions.queryKey };
}

export type getDataInstallationAuthorizationStatusResponse200 = {
  data: GetAuthorizationStatusResponse;
  status: 200;
};

export type getDataInstallationAuthorizationStatusResponse400 = {
  data: GetDataInstallationAuthorizationStatus400;
  status: 400;
};

export type getDataInstallationAuthorizationStatusResponse403 = {
  data: GetDataInstallationAuthorizationStatus403;
  status: 403;
};

export type getDataInstallationAuthorizationStatusResponse404 = {
  data: GetDataInstallationAuthorizationStatus404;
  status: 404;
};

export type getDataInstallationAuthorizationStatusResponseSuccess =
  getDataInstallationAuthorizationStatusResponse200 & {
    headers: Headers;
  };
export type getDataInstallationAuthorizationStatusResponseError = (
  | getDataInstallationAuthorizationStatusResponse400
  | getDataInstallationAuthorizationStatusResponse403
  | getDataInstallationAuthorizationStatusResponse404
) & {
  headers: Headers;
};

export type getDataInstallationAuthorizationStatusResponse =
  | getDataInstallationAuthorizationStatusResponseSuccess
  | getDataInstallationAuthorizationStatusResponseError;

export const getGetDataInstallationAuthorizationStatusUrl = (
  dataIntegrationId: string,
  state: string,
) => {
  return `https://api.workos.com/_widgets/DataIntegrations/${dataIntegrationId}/authorization-status/${state}`;
};

export const getDataInstallationAuthorizationStatus = async (
  dataIntegrationId: string,
  state: string,
  options?: RequestInit,
): Promise<getDataInstallationAuthorizationStatusResponse> => {
  const res = await fetch(
    getGetDataInstallationAuthorizationStatusUrl(dataIntegrationId, state),
    {
      ...options,
      method: "GET",
    },
  );

  const body = [204, 205, 304].includes(res.status) ? null : await res.text();

  const data: getDataInstallationAuthorizationStatusResponse["data"] = body
    ? JSON.parse(body)
    : {};
  return {
    data,
    status: res.status,
    headers: res.headers,
  } as getDataInstallationAuthorizationStatusResponse;
};

export const getGetDataInstallationAuthorizationStatusQueryKey = (
  dataIntegrationId: string,
  state: string,
) => {
  return [
    `https://api.workos.com/_widgets/DataIntegrations/${dataIntegrationId}/authorization-status/${state}`,
  ] as const;
};

export const getGetDataInstallationAuthorizationStatusQueryOptions = <
  TData = Awaited<ReturnType<typeof getDataInstallationAuthorizationStatus>>,
  TError =
    | GetDataInstallationAuthorizationStatus400
    | GetDataInstallationAuthorizationStatus403
    | GetDataInstallationAuthorizationStatus404,
>(
  dataIntegrationId: string,
  state: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getDataInstallationAuthorizationStatus>>,
        TError,
        TData
      >
    >;
    fetch?: RequestInit;
  },
) => {
  const { query: queryOptions, fetch: fetchOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getGetDataInstallationAuthorizationStatusQueryKey(dataIntegrationId, state);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getDataInstallationAuthorizationStatus>>
  > = ({ signal }) =>
    getDataInstallationAuthorizationStatus(dataIntegrationId, state, {
      signal,
      ...fetchOptions,
    });

  return {
    queryKey,
    queryFn,
    enabled: !!(dataIntegrationId && state),
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<ReturnType<typeof getDataInstallationAuthorizationStatus>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetDataInstallationAuthorizationStatusQueryResult = NonNullable<
  Awaited<ReturnType<typeof getDataInstallationAuthorizationStatus>>
>;
export type GetDataInstallationAuthorizationStatusQueryError =
  | GetDataInstallationAuthorizationStatus400
  | GetDataInstallationAuthorizationStatus403
  | GetDataInstallationAuthorizationStatus404;

export function useGetDataInstallationAuthorizationStatus<
  TData = Awaited<ReturnType<typeof getDataInstallationAuthorizationStatus>>,
  TError =
    | GetDataInstallationAuthorizationStatus400
    | GetDataInstallationAuthorizationStatus403
    | GetDataInstallationAuthorizationStatus404,
>(
  dataIntegrationId: string,
  state: string,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getDataInstallationAuthorizationStatus>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getDataInstallationAuthorizationStatus>>,
          TError,
          Awaited<ReturnType<typeof getDataInstallationAuthorizationStatus>>
        >,
        "initialData"
      >;
    fetch?: RequestInit;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetDataInstallationAuthorizationStatus<
  TData = Awaited<ReturnType<typeof getDataInstallationAuthorizationStatus>>,
  TError =
    | GetDataInstallationAuthorizationStatus400
    | GetDataInstallationAuthorizationStatus403
    | GetDataInstallationAuthorizationStatus404,
>(
  dataIntegrationId: string,
  state: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getDataInstallationAuthorizationStatus>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getDataInstallationAuthorizationStatus>>,
          TError,
          Awaited<ReturnType<typeof getDataInstallationAuthorizationStatus>>
        >,
        "initialData"
      >;
    fetch?: RequestInit;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetDataInstallationAuthorizationStatus<
  TData = Awaited<ReturnType<typeof getDataInstallationAuthorizationStatus>>,
  TError =
    | GetDataInstallationAuthorizationStatus400
    | GetDataInstallationAuthorizationStatus403
    | GetDataInstallationAuthorizationStatus404,
>(
  dataIntegrationId: string,
  state: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getDataInstallationAuthorizationStatus>>,
        TError,
        TData
      >
    >;
    fetch?: RequestInit;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};

export function useGetDataInstallationAuthorizationStatus<
  TData = Awaited<ReturnType<typeof getDataInstallationAuthorizationStatus>>,
  TError =
    | GetDataInstallationAuthorizationStatus400
    | GetDataInstallationAuthorizationStatus403
    | GetDataInstallationAuthorizationStatus404,
>(
  dataIntegrationId: string,
  state: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getDataInstallationAuthorizationStatus>>,
        TError,
        TData
      >
    >;
    fetch?: RequestInit;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions = getGetDataInstallationAuthorizationStatusQueryOptions(
    dataIntegrationId,
    state,
    options,
  );

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData, TError> };

  return { ...query, queryKey: queryOptions.queryKey };
}

export type getDataIntegrationAuthorizeUrlResponse200 = {
  data: GetDataIntegrationAuthorizeUrlResponse;
  status: 200;
};

export type getDataIntegrationAuthorizeUrlResponse400 = {
  data: GetDataIntegrationAuthorizeUrl400;
  status: 400;
};

export type getDataIntegrationAuthorizeUrlResponse403 = {
  data: GetDataIntegrationAuthorizeUrl403;
  status: 403;
};

export type getDataIntegrationAuthorizeUrlResponse404 = {
  data: GetDataIntegrationAuthorizeUrl404;
  status: 404;
};

export type getDataIntegrationAuthorizeUrlResponseSuccess =
  getDataIntegrationAuthorizeUrlResponse200 & {
    headers: Headers;
  };
export type getDataIntegrationAuthorizeUrlResponseError = (
  | getDataIntegrationAuthorizeUrlResponse400
  | getDataIntegrationAuthorizeUrlResponse403
  | getDataIntegrationAuthorizeUrlResponse404
) & {
  headers: Headers;
};

export type getDataIntegrationAuthorizeUrlResponse =
  | getDataIntegrationAuthorizeUrlResponseSuccess
  | getDataIntegrationAuthorizeUrlResponseError;

export const getGetDataIntegrationAuthorizeUrlUrl = (
  slug: string,
  params?: GetDataIntegrationAuthorizeUrlParams,
) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== undefined) {
      normalizedParams.append(key, value === null ? "null" : value.toString());
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0
    ? `https://api.workos.com/_widgets/DataIntegrations/${slug}/authorize?${stringifiedParams}`
    : `https://api.workos.com/_widgets/DataIntegrations/${slug}/authorize`;
};

export const getDataIntegrationAuthorizeUrl = async (
  slug: string,
  params?: GetDataIntegrationAuthorizeUrlParams,
  options?: RequestInit,
): Promise<getDataIntegrationAuthorizeUrlResponse> => {
  const res = await fetch(getGetDataIntegrationAuthorizeUrlUrl(slug, params), {
    ...options,
    method: "GET",
  });

  const body = [204, 205, 304].includes(res.status) ? null : await res.text();

  const data: getDataIntegrationAuthorizeUrlResponse["data"] = body
    ? JSON.parse(body)
    : {};
  return {
    data,
    status: res.status,
    headers: res.headers,
  } as getDataIntegrationAuthorizeUrlResponse;
};

export const getGetDataIntegrationAuthorizeUrlQueryKey = (
  slug: string,
  params?: GetDataIntegrationAuthorizeUrlParams,
) => {
  return [
    `https://api.workos.com/_widgets/DataIntegrations/${slug}/authorize`,
    ...(params ? [params] : []),
  ] as const;
};

export const getGetDataIntegrationAuthorizeUrlQueryOptions = <
  TData = Awaited<ReturnType<typeof getDataIntegrationAuthorizeUrl>>,
  TError =
    | GetDataIntegrationAuthorizeUrl400
    | GetDataIntegrationAuthorizeUrl403
    | GetDataIntegrationAuthorizeUrl404,
>(
  slug: string,
  params?: GetDataIntegrationAuthorizeUrlParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getDataIntegrationAuthorizeUrl>>,
        TError,
        TData
      >
    >;
    fetch?: RequestInit;
  },
) => {
  const { query: queryOptions, fetch: fetchOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getGetDataIntegrationAuthorizeUrlQueryKey(slug, params);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getDataIntegrationAuthorizeUrl>>
  > = ({ signal }) =>
    getDataIntegrationAuthorizeUrl(slug, params, { signal, ...fetchOptions });

  return {
    queryKey,
    queryFn,
    enabled: !!slug,
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<ReturnType<typeof getDataIntegrationAuthorizeUrl>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetDataIntegrationAuthorizeUrlQueryResult = NonNullable<
  Awaited<ReturnType<typeof getDataIntegrationAuthorizeUrl>>
>;
export type GetDataIntegrationAuthorizeUrlQueryError =
  | GetDataIntegrationAuthorizeUrl400
  | GetDataIntegrationAuthorizeUrl403
  | GetDataIntegrationAuthorizeUrl404;

export function useGetDataIntegrationAuthorizeUrl<
  TData = Awaited<ReturnType<typeof getDataIntegrationAuthorizeUrl>>,
  TError =
    | GetDataIntegrationAuthorizeUrl400
    | GetDataIntegrationAuthorizeUrl403
    | GetDataIntegrationAuthorizeUrl404,
>(
  slug: string,
  params: undefined | GetDataIntegrationAuthorizeUrlParams,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getDataIntegrationAuthorizeUrl>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getDataIntegrationAuthorizeUrl>>,
          TError,
          Awaited<ReturnType<typeof getDataIntegrationAuthorizeUrl>>
        >,
        "initialData"
      >;
    fetch?: RequestInit;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetDataIntegrationAuthorizeUrl<
  TData = Awaited<ReturnType<typeof getDataIntegrationAuthorizeUrl>>,
  TError =
    | GetDataIntegrationAuthorizeUrl400
    | GetDataIntegrationAuthorizeUrl403
    | GetDataIntegrationAuthorizeUrl404,
>(
  slug: string,
  params?: GetDataIntegrationAuthorizeUrlParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getDataIntegrationAuthorizeUrl>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getDataIntegrationAuthorizeUrl>>,
          TError,
          Awaited<ReturnType<typeof getDataIntegrationAuthorizeUrl>>
        >,
        "initialData"
      >;
    fetch?: RequestInit;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetDataIntegrationAuthorizeUrl<
  TData = Awaited<ReturnType<typeof getDataIntegrationAuthorizeUrl>>,
  TError =
    | GetDataIntegrationAuthorizeUrl400
    | GetDataIntegrationAuthorizeUrl403
    | GetDataIntegrationAuthorizeUrl404,
>(
  slug: string,
  params?: GetDataIntegrationAuthorizeUrlParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getDataIntegrationAuthorizeUrl>>,
        TError,
        TData
      >
    >;
    fetch?: RequestInit;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};

export function useGetDataIntegrationAuthorizeUrl<
  TData = Awaited<ReturnType<typeof getDataIntegrationAuthorizeUrl>>,
  TError =
    | GetDataIntegrationAuthorizeUrl400
    | GetDataIntegrationAuthorizeUrl403
    | GetDataIntegrationAuthorizeUrl404,
>(
  slug: string,
  params?: GetDataIntegrationAuthorizeUrlParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getDataIntegrationAuthorizeUrl>>,
        TError,
        TData
      >
    >;
    fetch?: RequestInit;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions = getGetDataIntegrationAuthorizeUrlQueryOptions(
    slug,
    params,
    options,
  );

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData, TError> };

  return { ...query, queryKey: queryOptions.queryKey };
}

export type createUserApiKeyResponse201 = {
  data: CreateUserApiKeyResponse;
  status: 201;
};

export type createUserApiKeyResponse400 = {
  data: CreateUserApiKey400;
  status: 400;
};

export type createUserApiKeyResponse403 = {
  data: CreateUserApiKey403;
  status: 403;
};

export type createUserApiKeyResponse404 = {
  data: CreateUserApiKey404;
  status: 404;
};

export type createUserApiKeyResponse422 = {
  data: CreateUserApiKey422;
  status: 422;
};

export type createUserApiKeyResponseSuccess = createUserApiKeyResponse201 & {
  headers: Headers;
};
export type createUserApiKeyResponseError = (
  | createUserApiKeyResponse400
  | createUserApiKeyResponse403
  | createUserApiKeyResponse404
  | createUserApiKeyResponse422
) & {
  headers: Headers;
};

export type createUserApiKeyResponse =
  | createUserApiKeyResponseSuccess
  | createUserApiKeyResponseError;

export const getCreateUserApiKeyUrl = () => {
  return `https://api.workos.com/_widgets/UserApiKeys/api-keys`;
};

export const createUserApiKey = async (
  createUserApiKeyRequest: CreateUserApiKeyRequest,
  options?: RequestInit,
): Promise<createUserApiKeyResponse> => {
  const res = await fetch(getCreateUserApiKeyUrl(), {
    ...options,
    method: "POST",
    headers: { "Content-Type": "application/json", ...options?.headers },
    body: JSON.stringify(createUserApiKeyRequest),
  });

  const body = [204, 205, 304].includes(res.status) ? null : await res.text();

  const data: createUserApiKeyResponse["data"] = body ? JSON.parse(body) : {};
  return {
    data,
    status: res.status,
    headers: res.headers,
  } as createUserApiKeyResponse;
};

export const getCreateUserApiKeyMutationOptions = <
  TError =
    | CreateUserApiKey400
    | CreateUserApiKey403
    | CreateUserApiKey404
    | CreateUserApiKey422,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof createUserApiKey>>,
    TError,
    { data: CreateUserApiKeyRequest },
    TContext
  >;
  fetch?: RequestInit;
}): UseMutationOptions<
  Awaited<ReturnType<typeof createUserApiKey>>,
  TError,
  { data: CreateUserApiKeyRequest },
  TContext
> => {
  const mutationKey = ["createUserApiKey"];
  const { mutation: mutationOptions, fetch: fetchOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, fetch: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof createUserApiKey>>,
    { data: CreateUserApiKeyRequest }
  > = (props) => {
    const { data } = props ?? {};

    return createUserApiKey(data, fetchOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type CreateUserApiKeyMutationResult = NonNullable<
  Awaited<ReturnType<typeof createUserApiKey>>
>;
export type CreateUserApiKeyMutationBody = CreateUserApiKeyRequest;
export type CreateUserApiKeyMutationError =
  | CreateUserApiKey400
  | CreateUserApiKey403
  | CreateUserApiKey404
  | CreateUserApiKey422;

export const useCreateUserApiKey = <
  TError =
    | CreateUserApiKey400
    | CreateUserApiKey403
    | CreateUserApiKey404
    | CreateUserApiKey422,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof createUserApiKey>>,
      TError,
      { data: CreateUserApiKeyRequest },
      TContext
    >;
    fetch?: RequestInit;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof createUserApiKey>>,
  TError,
  { data: CreateUserApiKeyRequest },
  TContext
> => {
  return useMutation(getCreateUserApiKeyMutationOptions(options), queryClient);
};

export type listUserApiKeysResponse200 = {
  data: ListUserApiKeysResponse;
  status: 200;
};

export type listUserApiKeysResponse400 = {
  data: ListUserApiKeys400;
  status: 400;
};

export type listUserApiKeysResponse403 = {
  data: ListUserApiKeys403;
  status: 403;
};

export type listUserApiKeysResponseSuccess = listUserApiKeysResponse200 & {
  headers: Headers;
};
export type listUserApiKeysResponseError = (
  | listUserApiKeysResponse400
  | listUserApiKeysResponse403
) & {
  headers: Headers;
};

export type listUserApiKeysResponse =
  | listUserApiKeysResponseSuccess
  | listUserApiKeysResponseError;

export const getListUserApiKeysUrl = (params?: ListUserApiKeysParams) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== undefined) {
      normalizedParams.append(key, value === null ? "null" : value.toString());
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0
    ? `https://api.workos.com/_widgets/UserApiKeys/api-keys?${stringifiedParams}`
    : `https://api.workos.com/_widgets/UserApiKeys/api-keys`;
};

export const listUserApiKeys = async (
  params?: ListUserApiKeysParams,
  options?: RequestInit,
): Promise<listUserApiKeysResponse> => {
  const res = await fetch(getListUserApiKeysUrl(params), {
    ...options,
    method: "GET",
  });

  const body = [204, 205, 304].includes(res.status) ? null : await res.text();

  const data: listUserApiKeysResponse["data"] = body ? JSON.parse(body) : {};
  return {
    data,
    status: res.status,
    headers: res.headers,
  } as listUserApiKeysResponse;
};

export const getListUserApiKeysQueryKey = (params?: ListUserApiKeysParams) => {
  return [
    `https://api.workos.com/_widgets/UserApiKeys/api-keys`,
    ...(params ? [params] : []),
  ] as const;
};

export const getListUserApiKeysQueryOptions = <
  TData = Awaited<ReturnType<typeof listUserApiKeys>>,
  TError = ListUserApiKeys400 | ListUserApiKeys403,
>(
  params?: ListUserApiKeysParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof listUserApiKeys>>,
        TError,
        TData
      >
    >;
    fetch?: RequestInit;
  },
) => {
  const { query: queryOptions, fetch: fetchOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getListUserApiKeysQueryKey(params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof listUserApiKeys>>> = ({
    signal,
  }) => listUserApiKeys(params, { signal, ...fetchOptions });

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof listUserApiKeys>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type ListUserApiKeysQueryResult = NonNullable<
  Awaited<ReturnType<typeof listUserApiKeys>>
>;
export type ListUserApiKeysQueryError = ListUserApiKeys400 | ListUserApiKeys403;

export function useListUserApiKeys<
  TData = Awaited<ReturnType<typeof listUserApiKeys>>,
  TError = ListUserApiKeys400 | ListUserApiKeys403,
>(
  params: undefined | ListUserApiKeysParams,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof listUserApiKeys>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listUserApiKeys>>,
          TError,
          Awaited<ReturnType<typeof listUserApiKeys>>
        >,
        "initialData"
      >;
    fetch?: RequestInit;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useListUserApiKeys<
  TData = Awaited<ReturnType<typeof listUserApiKeys>>,
  TError = ListUserApiKeys400 | ListUserApiKeys403,
>(
  params?: ListUserApiKeysParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof listUserApiKeys>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listUserApiKeys>>,
          TError,
          Awaited<ReturnType<typeof listUserApiKeys>>
        >,
        "initialData"
      >;
    fetch?: RequestInit;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useListUserApiKeys<
  TData = Awaited<ReturnType<typeof listUserApiKeys>>,
  TError = ListUserApiKeys400 | ListUserApiKeys403,
>(
  params?: ListUserApiKeysParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof listUserApiKeys>>,
        TError,
        TData
      >
    >;
    fetch?: RequestInit;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};

export function useListUserApiKeys<
  TData = Awaited<ReturnType<typeof listUserApiKeys>>,
  TError = ListUserApiKeys400 | ListUserApiKeys403,
>(
  params?: ListUserApiKeysParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof listUserApiKeys>>,
        TError,
        TData
      >
    >;
    fetch?: RequestInit;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions = getListUserApiKeysQueryOptions(params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData, TError> };

  return { ...query, queryKey: queryOptions.queryKey };
}

export type listUserApiKeyPermissionsResponse200 = {
  data: ListUserApiKeyPermissionsResponse;
  status: 200;
};

export type listUserApiKeyPermissionsResponse400 = {
  data: ListUserApiKeyPermissions400;
  status: 400;
};

export type listUserApiKeyPermissionsResponse403 = {
  data: ListUserApiKeyPermissions403;
  status: 403;
};

export type listUserApiKeyPermissionsResponseSuccess =
  listUserApiKeyPermissionsResponse200 & {
    headers: Headers;
  };
export type listUserApiKeyPermissionsResponseError = (
  | listUserApiKeyPermissionsResponse400
  | listUserApiKeyPermissionsResponse403
) & {
  headers: Headers;
};

export type listUserApiKeyPermissionsResponse =
  | listUserApiKeyPermissionsResponseSuccess
  | listUserApiKeyPermissionsResponseError;

export const getListUserApiKeyPermissionsUrl = (
  params?: ListUserApiKeyPermissionsParams,
) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== undefined) {
      normalizedParams.append(key, value === null ? "null" : value.toString());
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0
    ? `https://api.workos.com/_widgets/UserApiKeys/permissions?${stringifiedParams}`
    : `https://api.workos.com/_widgets/UserApiKeys/permissions`;
};

export const listUserApiKeyPermissions = async (
  params?: ListUserApiKeyPermissionsParams,
  options?: RequestInit,
): Promise<listUserApiKeyPermissionsResponse> => {
  const res = await fetch(getListUserApiKeyPermissionsUrl(params), {
    ...options,
    method: "GET",
  });

  const body = [204, 205, 304].includes(res.status) ? null : await res.text();

  const data: listUserApiKeyPermissionsResponse["data"] = body
    ? JSON.parse(body)
    : {};
  return {
    data,
    status: res.status,
    headers: res.headers,
  } as listUserApiKeyPermissionsResponse;
};

export const getListUserApiKeyPermissionsQueryKey = (
  params?: ListUserApiKeyPermissionsParams,
) => {
  return [
    `https://api.workos.com/_widgets/UserApiKeys/permissions`,
    ...(params ? [params] : []),
  ] as const;
};

export const getListUserApiKeyPermissionsQueryOptions = <
  TData = Awaited<ReturnType<typeof listUserApiKeyPermissions>>,
  TError = ListUserApiKeyPermissions400 | ListUserApiKeyPermissions403,
>(
  params?: ListUserApiKeyPermissionsParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof listUserApiKeyPermissions>>,
        TError,
        TData
      >
    >;
    fetch?: RequestInit;
  },
) => {
  const { query: queryOptions, fetch: fetchOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getListUserApiKeyPermissionsQueryKey(params);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof listUserApiKeyPermissions>>
  > = ({ signal }) =>
    listUserApiKeyPermissions(params, { signal, ...fetchOptions });

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof listUserApiKeyPermissions>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type ListUserApiKeyPermissionsQueryResult = NonNullable<
  Awaited<ReturnType<typeof listUserApiKeyPermissions>>
>;
export type ListUserApiKeyPermissionsQueryError =
  | ListUserApiKeyPermissions400
  | ListUserApiKeyPermissions403;

export function useListUserApiKeyPermissions<
  TData = Awaited<ReturnType<typeof listUserApiKeyPermissions>>,
  TError = ListUserApiKeyPermissions400 | ListUserApiKeyPermissions403,
>(
  params: undefined | ListUserApiKeyPermissionsParams,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof listUserApiKeyPermissions>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listUserApiKeyPermissions>>,
          TError,
          Awaited<ReturnType<typeof listUserApiKeyPermissions>>
        >,
        "initialData"
      >;
    fetch?: RequestInit;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useListUserApiKeyPermissions<
  TData = Awaited<ReturnType<typeof listUserApiKeyPermissions>>,
  TError = ListUserApiKeyPermissions400 | ListUserApiKeyPermissions403,
>(
  params?: ListUserApiKeyPermissionsParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof listUserApiKeyPermissions>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listUserApiKeyPermissions>>,
          TError,
          Awaited<ReturnType<typeof listUserApiKeyPermissions>>
        >,
        "initialData"
      >;
    fetch?: RequestInit;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useListUserApiKeyPermissions<
  TData = Awaited<ReturnType<typeof listUserApiKeyPermissions>>,
  TError = ListUserApiKeyPermissions400 | ListUserApiKeyPermissions403,
>(
  params?: ListUserApiKeyPermissionsParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof listUserApiKeyPermissions>>,
        TError,
        TData
      >
    >;
    fetch?: RequestInit;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};

export function useListUserApiKeyPermissions<
  TData = Awaited<ReturnType<typeof listUserApiKeyPermissions>>,
  TError = ListUserApiKeyPermissions400 | ListUserApiKeyPermissions403,
>(
  params?: ListUserApiKeyPermissionsParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof listUserApiKeyPermissions>>,
        TError,
        TData
      >
    >;
    fetch?: RequestInit;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions = getListUserApiKeyPermissionsQueryOptions(
    params,
    options,
  );

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData, TError> };

  return { ...query, queryKey: queryOptions.queryKey };
}

export type deleteUserApiKeyResponse200 = {
  data: DeleteUserApiKey200;
  status: 200;
};

export type deleteUserApiKeyResponse403 = {
  data: DeleteUserApiKey403;
  status: 403;
};

export type deleteUserApiKeyResponse404 = {
  data: DeleteUserApiKey404;
  status: 404;
};

export type deleteUserApiKeyResponseSuccess = deleteUserApiKeyResponse200 & {
  headers: Headers;
};
export type deleteUserApiKeyResponseError = (
  | deleteUserApiKeyResponse403
  | deleteUserApiKeyResponse404
) & {
  headers: Headers;
};

export type deleteUserApiKeyResponse =
  | deleteUserApiKeyResponseSuccess
  | deleteUserApiKeyResponseError;

export const getDeleteUserApiKeyUrl = (apiKeyId: string) => {
  return `https://api.workos.com/_widgets/UserApiKeys/${apiKeyId}`;
};

export const deleteUserApiKey = async (
  apiKeyId: string,
  options?: RequestInit,
): Promise<deleteUserApiKeyResponse> => {
  const res = await fetch(getDeleteUserApiKeyUrl(apiKeyId), {
    ...options,
    method: "DELETE",
  });

  const body = [204, 205, 304].includes(res.status) ? null : await res.text();

  const data: deleteUserApiKeyResponse["data"] = body ? JSON.parse(body) : {};
  return {
    data,
    status: res.status,
    headers: res.headers,
  } as deleteUserApiKeyResponse;
};

export const getDeleteUserApiKeyMutationOptions = <
  TError = DeleteUserApiKey403 | DeleteUserApiKey404,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof deleteUserApiKey>>,
    TError,
    { apiKeyId: string },
    TContext
  >;
  fetch?: RequestInit;
}): UseMutationOptions<
  Awaited<ReturnType<typeof deleteUserApiKey>>,
  TError,
  { apiKeyId: string },
  TContext
> => {
  const mutationKey = ["deleteUserApiKey"];
  const { mutation: mutationOptions, fetch: fetchOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, fetch: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof deleteUserApiKey>>,
    { apiKeyId: string }
  > = (props) => {
    const { apiKeyId } = props ?? {};

    return deleteUserApiKey(apiKeyId, fetchOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type DeleteUserApiKeyMutationResult = NonNullable<
  Awaited<ReturnType<typeof deleteUserApiKey>>
>;

export type DeleteUserApiKeyMutationError =
  | DeleteUserApiKey403
  | DeleteUserApiKey404;

export const useDeleteUserApiKey = <
  TError = DeleteUserApiKey403 | DeleteUserApiKey404,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof deleteUserApiKey>>,
      TError,
      { apiKeyId: string },
      TContext
    >;
    fetch?: RequestInit;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof deleteUserApiKey>>,
  TError,
  { apiKeyId: string },
  TContext
> => {
  return useMutation(getDeleteUserApiKeyMutationOptions(options), queryClient);
};

/**
 * Sends an invitation email to a user to join the organization. If the user does not have an account, they will be prompted to create one upon accepting.
 */
export type inviteMemberResponse201 = {
  data: InviteMemberResponse;
  status: 201;
};

export type inviteMemberResponse400 = {
  data: InviteMember400;
  status: 400;
};

export type inviteMemberResponse403 = {
  data: InviteMember403;
  status: 403;
};

export type inviteMemberResponse404 = {
  data: InviteMember404;
  status: 404;
};

export type inviteMemberResponseSuccess = inviteMemberResponse201 & {
  headers: Headers;
};
export type inviteMemberResponseError = (
  | inviteMemberResponse400
  | inviteMemberResponse403
  | inviteMemberResponse404
) & {
  headers: Headers;
};

export type inviteMemberResponse =
  | inviteMemberResponseSuccess
  | inviteMemberResponseError;

export const getInviteMemberUrl = () => {
  return `https://api.workos.com/_widgets/UserManagement/invite-user`;
};

export const inviteMember = async (
  inviteMemberBody: InviteMemberBody,
  options?: RequestInit,
): Promise<inviteMemberResponse> => {
  const res = await fetch(getInviteMemberUrl(), {
    ...options,
    method: "POST",
    headers: { "Content-Type": "application/json", ...options?.headers },
    body: JSON.stringify(inviteMemberBody),
  });

  const body = [204, 205, 304].includes(res.status) ? null : await res.text();

  const data: inviteMemberResponse["data"] = body ? JSON.parse(body) : {};
  return {
    data,
    status: res.status,
    headers: res.headers,
  } as inviteMemberResponse;
};

export const getInviteMemberMutationOptions = <
  TError = InviteMember400 | InviteMember403 | InviteMember404,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof inviteMember>>,
    TError,
    { data: InviteMemberBody },
    TContext
  >;
  fetch?: RequestInit;
}): UseMutationOptions<
  Awaited<ReturnType<typeof inviteMember>>,
  TError,
  { data: InviteMemberBody },
  TContext
> => {
  const mutationKey = ["inviteMember"];
  const { mutation: mutationOptions, fetch: fetchOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, fetch: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof inviteMember>>,
    { data: InviteMemberBody }
  > = (props) => {
    const { data } = props ?? {};

    return inviteMember(data, fetchOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type InviteMemberMutationResult = NonNullable<
  Awaited<ReturnType<typeof inviteMember>>
>;
export type InviteMemberMutationBody = InviteMemberBody;
export type InviteMemberMutationError =
  | InviteMember400
  | InviteMember403
  | InviteMember404;

export const useInviteMember = <
  TError = InviteMember400 | InviteMember403 | InviteMember404,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof inviteMember>>,
      TError,
      { data: InviteMemberBody },
      TContext
    >;
    fetch?: RequestInit;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof inviteMember>>,
  TError,
  { data: InviteMemberBody },
  TContext
> => {
  return useMutation(getInviteMemberMutationOptions(options), queryClient);
};

/**
 * Cancels a pending invitation for the specified user, preventing them from joining the organization via that invite link.
 */
export type revokeInviteResponse200 = {
  data: RevokeMemberResponse;
  status: 200;
};

export type revokeInviteResponse400 = {
  data: RevokeInvite400;
  status: 400;
};

export type revokeInviteResponse403 = {
  data: RevokeInvite403;
  status: 403;
};

export type revokeInviteResponse404 = {
  data: RevokeInvite404;
  status: 404;
};

export type revokeInviteResponseSuccess = revokeInviteResponse200 & {
  headers: Headers;
};
export type revokeInviteResponseError = (
  | revokeInviteResponse400
  | revokeInviteResponse403
  | revokeInviteResponse404
) & {
  headers: Headers;
};

export type revokeInviteResponse =
  | revokeInviteResponseSuccess
  | revokeInviteResponseError;

export const getRevokeInviteUrl = (userId: string) => {
  return `https://api.workos.com/_widgets/UserManagement/invites/${userId}`;
};

export const revokeInvite = async (
  userId: string,
  options?: RequestInit,
): Promise<revokeInviteResponse> => {
  const res = await fetch(getRevokeInviteUrl(userId), {
    ...options,
    method: "DELETE",
  });

  const body = [204, 205, 304].includes(res.status) ? null : await res.text();

  const data: revokeInviteResponse["data"] = body ? JSON.parse(body) : {};
  return {
    data,
    status: res.status,
    headers: res.headers,
  } as revokeInviteResponse;
};

export const getRevokeInviteMutationOptions = <
  TError = RevokeInvite400 | RevokeInvite403 | RevokeInvite404,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof revokeInvite>>,
    TError,
    { userId: string },
    TContext
  >;
  fetch?: RequestInit;
}): UseMutationOptions<
  Awaited<ReturnType<typeof revokeInvite>>,
  TError,
  { userId: string },
  TContext
> => {
  const mutationKey = ["revokeInvite"];
  const { mutation: mutationOptions, fetch: fetchOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, fetch: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof revokeInvite>>,
    { userId: string }
  > = (props) => {
    const { userId } = props ?? {};

    return revokeInvite(userId, fetchOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type RevokeInviteMutationResult = NonNullable<
  Awaited<ReturnType<typeof revokeInvite>>
>;

export type RevokeInviteMutationError =
  | RevokeInvite400
  | RevokeInvite403
  | RevokeInvite404;

export const useRevokeInvite = <
  TError = RevokeInvite400 | RevokeInvite403 | RevokeInvite404,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof revokeInvite>>,
      TError,
      { userId: string },
      TContext
    >;
    fetch?: RequestInit;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof revokeInvite>>,
  TError,
  { userId: string },
  TContext
> => {
  return useMutation(getRevokeInviteMutationOptions(options), queryClient);
};

/**
 * Resends the pending invitation email to the specified user. Returns an error if the invitation has already been accepted or has expired.
 */
export type resendInviteResponse201 = {
  data: ResendInviteResponse;
  status: 201;
};

export type resendInviteResponse400 = {
  data: ResendInvite400;
  status: 400;
};

export type resendInviteResponse403 = {
  data: ResendInvite403;
  status: 403;
};

export type resendInviteResponse404 = {
  data: ResendInvite404;
  status: 404;
};

export type resendInviteResponse422 = {
  data: ResendInvite422;
  status: 422;
};

export type resendInviteResponseSuccess = resendInviteResponse201 & {
  headers: Headers;
};
export type resendInviteResponseError = (
  | resendInviteResponse400
  | resendInviteResponse403
  | resendInviteResponse404
  | resendInviteResponse422
) & {
  headers: Headers;
};

export type resendInviteResponse =
  | resendInviteResponseSuccess
  | resendInviteResponseError;

export const getResendInviteUrl = (userId: string) => {
  return `https://api.workos.com/_widgets/UserManagement/invites/${userId}/resend`;
};

export const resendInvite = async (
  userId: string,
  options?: RequestInit,
): Promise<resendInviteResponse> => {
  const res = await fetch(getResendInviteUrl(userId), {
    ...options,
    method: "POST",
  });

  const body = [204, 205, 304].includes(res.status) ? null : await res.text();

  const data: resendInviteResponse["data"] = body ? JSON.parse(body) : {};
  return {
    data,
    status: res.status,
    headers: res.headers,
  } as resendInviteResponse;
};

export const getResendInviteMutationOptions = <
  TError =
    | ResendInvite400
    | ResendInvite403
    | ResendInvite404
    | ResendInvite422,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof resendInvite>>,
    TError,
    { userId: string },
    TContext
  >;
  fetch?: RequestInit;
}): UseMutationOptions<
  Awaited<ReturnType<typeof resendInvite>>,
  TError,
  { userId: string },
  TContext
> => {
  const mutationKey = ["resendInvite"];
  const { mutation: mutationOptions, fetch: fetchOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, fetch: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof resendInvite>>,
    { userId: string }
  > = (props) => {
    const { userId } = props ?? {};

    return resendInvite(userId, fetchOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type ResendInviteMutationResult = NonNullable<
  Awaited<ReturnType<typeof resendInvite>>
>;

export type ResendInviteMutationError =
  | ResendInvite400
  | ResendInvite403
  | ResendInvite404
  | ResendInvite422;

export const useResendInvite = <
  TError =
    | ResendInvite400
    | ResendInvite403
    | ResendInvite404
    | ResendInvite422,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof resendInvite>>,
      TError,
      { userId: string },
      TContext
    >;
    fetch?: RequestInit;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof resendInvite>>,
  TError,
  { userId: string },
  TContext
> => {
  return useMutation(getResendInviteMutationOptions(options), queryClient);
};

/**
 * Returns a paginated list of members belonging to the organization. Supports filtering by search term and role, and cursor-based pagination via before/after parameters.
 */
export type membersResponse200 = {
  data: MembersResponse;
  status: 200;
};

export type membersResponse403 = {
  data: Members403;
  status: 403;
};

export type membersResponse404 = {
  data: Members404;
  status: 404;
};

export type membersResponseSuccess = membersResponse200 & {
  headers: Headers;
};
export type membersResponseError = (membersResponse403 | membersResponse404) & {
  headers: Headers;
};

export type membersResponse = membersResponseSuccess | membersResponseError;

export const getMembersUrl = (params?: MembersParams) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== undefined) {
      normalizedParams.append(key, value === null ? "null" : value.toString());
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0
    ? `https://api.workos.com/_widgets/UserManagement/members?${stringifiedParams}`
    : `https://api.workos.com/_widgets/UserManagement/members`;
};

export const members = async (
  params?: MembersParams,
  options?: RequestInit,
): Promise<membersResponse> => {
  const res = await fetch(getMembersUrl(params), {
    ...options,
    method: "GET",
  });

  const body = [204, 205, 304].includes(res.status) ? null : await res.text();

  const data: membersResponse["data"] = body ? JSON.parse(body) : {};
  return { data, status: res.status, headers: res.headers } as membersResponse;
};

export const getMembersQueryKey = (params?: MembersParams) => {
  return [
    `https://api.workos.com/_widgets/UserManagement/members`,
    ...(params ? [params] : []),
  ] as const;
};

export const getMembersQueryOptions = <
  TData = Awaited<ReturnType<typeof members>>,
  TError = Members403 | Members404,
>(
  params?: MembersParams,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof members>>, TError, TData>
    >;
    fetch?: RequestInit;
  },
) => {
  const { query: queryOptions, fetch: fetchOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getMembersQueryKey(params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof members>>> = ({
    signal,
  }) => members(params, { signal, ...fetchOptions });

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof members>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type MembersQueryResult = NonNullable<
  Awaited<ReturnType<typeof members>>
>;
export type MembersQueryError = Members403 | Members404;

export function useMembers<
  TData = Awaited<ReturnType<typeof members>>,
  TError = Members403 | Members404,
>(
  params: undefined | MembersParams,
  options: {
    query: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof members>>, TError, TData>
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof members>>,
          TError,
          Awaited<ReturnType<typeof members>>
        >,
        "initialData"
      >;
    fetch?: RequestInit;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useMembers<
  TData = Awaited<ReturnType<typeof members>>,
  TError = Members403 | Members404,
>(
  params?: MembersParams,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof members>>, TError, TData>
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof members>>,
          TError,
          Awaited<ReturnType<typeof members>>
        >,
        "initialData"
      >;
    fetch?: RequestInit;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useMembers<
  TData = Awaited<ReturnType<typeof members>>,
  TError = Members403 | Members404,
>(
  params?: MembersParams,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof members>>, TError, TData>
    >;
    fetch?: RequestInit;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};

export function useMembers<
  TData = Awaited<ReturnType<typeof members>>,
  TError = Members403 | Members404,
>(
  params?: MembersParams,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof members>>, TError, TData>
    >;
    fetch?: RequestInit;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions = getMembersQueryOptions(params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData, TError> };

  return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * Removes the specified user from the organization by revoking their membership. The user account itself is not deleted.
 */
export type removeMemberResponse200 = {
  data: RemoveMemberResponse;
  status: 200;
};

export type removeMemberResponse400 = {
  data: RemoveMember400;
  status: 400;
};

export type removeMemberResponse403 = {
  data: RemoveMember403;
  status: 403;
};

export type removeMemberResponse404 = {
  data: RemoveMember404;
  status: 404;
};

export type removeMemberResponseSuccess = removeMemberResponse200 & {
  headers: Headers;
};
export type removeMemberResponseError = (
  | removeMemberResponse400
  | removeMemberResponse403
  | removeMemberResponse404
) & {
  headers: Headers;
};

export type removeMemberResponse =
  | removeMemberResponseSuccess
  | removeMemberResponseError;

export const getRemoveMemberUrl = (userId: string) => {
  return `https://api.workos.com/_widgets/UserManagement/members/${userId}`;
};

export const removeMember = async (
  userId: string,
  options?: RequestInit,
): Promise<removeMemberResponse> => {
  const res = await fetch(getRemoveMemberUrl(userId), {
    ...options,
    method: "DELETE",
  });

  const body = [204, 205, 304].includes(res.status) ? null : await res.text();

  const data: removeMemberResponse["data"] = body ? JSON.parse(body) : {};
  return {
    data,
    status: res.status,
    headers: res.headers,
  } as removeMemberResponse;
};

export const getRemoveMemberMutationOptions = <
  TError = RemoveMember400 | RemoveMember403 | RemoveMember404,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof removeMember>>,
    TError,
    { userId: string },
    TContext
  >;
  fetch?: RequestInit;
}): UseMutationOptions<
  Awaited<ReturnType<typeof removeMember>>,
  TError,
  { userId: string },
  TContext
> => {
  const mutationKey = ["removeMember"];
  const { mutation: mutationOptions, fetch: fetchOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, fetch: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof removeMember>>,
    { userId: string }
  > = (props) => {
    const { userId } = props ?? {};

    return removeMember(userId, fetchOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type RemoveMemberMutationResult = NonNullable<
  Awaited<ReturnType<typeof removeMember>>
>;

export type RemoveMemberMutationError =
  | RemoveMember400
  | RemoveMember403
  | RemoveMember404;

export const useRemoveMember = <
  TError = RemoveMember400 | RemoveMember403 | RemoveMember404,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof removeMember>>,
      TError,
      { userId: string },
      TContext
    >;
    fetch?: RequestInit;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof removeMember>>,
  TError,
  { userId: string },
  TContext
> => {
  return useMutation(getRemoveMemberMutationOptions(options), queryClient);
};

/**
 * Updates the specified member's organization membership, such as changing their assigned role.
 */
export type updateMemberResponse200 = {
  data: UpdateMemberResponse;
  status: 200;
};

export type updateMemberResponse400 = {
  data: UpdateMember400;
  status: 400;
};

export type updateMemberResponse403 = {
  data: UpdateMember403;
  status: 403;
};

export type updateMemberResponse404 = {
  data: UpdateMember404;
  status: 404;
};

export type updateMemberResponse422 = {
  data: UpdateMember422;
  status: 422;
};

export type updateMemberResponseSuccess = updateMemberResponse200 & {
  headers: Headers;
};
export type updateMemberResponseError = (
  | updateMemberResponse400
  | updateMemberResponse403
  | updateMemberResponse404
  | updateMemberResponse422
) & {
  headers: Headers;
};

export type updateMemberResponse =
  | updateMemberResponseSuccess
  | updateMemberResponseError;

export const getUpdateMemberUrl = (userId: string) => {
  return `https://api.workos.com/_widgets/UserManagement/members/${userId}`;
};

export const updateMember = async (
  userId: string,
  updateMemberBody: UpdateMemberBody,
  options?: RequestInit,
): Promise<updateMemberResponse> => {
  const res = await fetch(getUpdateMemberUrl(userId), {
    ...options,
    method: "POST",
    headers: { "Content-Type": "application/json", ...options?.headers },
    body: JSON.stringify(updateMemberBody),
  });

  const body = [204, 205, 304].includes(res.status) ? null : await res.text();

  const data: updateMemberResponse["data"] = body ? JSON.parse(body) : {};
  return {
    data,
    status: res.status,
    headers: res.headers,
  } as updateMemberResponse;
};

export const getUpdateMemberMutationOptions = <
  TError =
    | UpdateMember400
    | UpdateMember403
    | UpdateMember404
    | UpdateMember422,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof updateMember>>,
    TError,
    { userId: string; data: UpdateMemberBody },
    TContext
  >;
  fetch?: RequestInit;
}): UseMutationOptions<
  Awaited<ReturnType<typeof updateMember>>,
  TError,
  { userId: string; data: UpdateMemberBody },
  TContext
> => {
  const mutationKey = ["updateMember"];
  const { mutation: mutationOptions, fetch: fetchOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, fetch: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof updateMember>>,
    { userId: string; data: UpdateMemberBody }
  > = (props) => {
    const { userId, data } = props ?? {};

    return updateMember(userId, data, fetchOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type UpdateMemberMutationResult = NonNullable<
  Awaited<ReturnType<typeof updateMember>>
>;
export type UpdateMemberMutationBody = UpdateMemberBody;
export type UpdateMemberMutationError =
  | UpdateMember400
  | UpdateMember403
  | UpdateMember404
  | UpdateMember422;

export const useUpdateMember = <
  TError =
    | UpdateMember400
    | UpdateMember403
    | UpdateMember404
    | UpdateMember422,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof updateMember>>,
      TError,
      { userId: string; data: UpdateMemberBody },
      TContext
    >;
    fetch?: RequestInit;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof updateMember>>,
  TError,
  { userId: string; data: UpdateMemberBody },
  TContext
> => {
  return useMutation(getUpdateMemberMutationOptions(options), queryClient);
};

/**
 * Returns the list of organizations the authenticated user is a member of.
 */
export type organizationsResponse200 = {
  data: OrganizationsResponse;
  status: 200;
};

export type organizationsResponse403 = {
  data: Organizations403;
  status: 403;
};

export type organizationsResponse404 = {
  data: Organizations404;
  status: 404;
};

export type organizationsResponseSuccess = organizationsResponse200 & {
  headers: Headers;
};
export type organizationsResponseError = (
  | organizationsResponse403
  | organizationsResponse404
) & {
  headers: Headers;
};

export type organizationsResponse =
  | organizationsResponseSuccess
  | organizationsResponseError;

export const getOrganizationsUrl = () => {
  return `https://api.workos.com/_widgets/UserManagement/organizations`;
};

export const organizations = async (
  options?: RequestInit,
): Promise<organizationsResponse> => {
  const res = await fetch(getOrganizationsUrl(), {
    ...options,
    method: "GET",
  });

  const body = [204, 205, 304].includes(res.status) ? null : await res.text();

  const data: organizationsResponse["data"] = body ? JSON.parse(body) : {};
  return {
    data,
    status: res.status,
    headers: res.headers,
  } as organizationsResponse;
};

export const getOrganizationsQueryKey = () => {
  return [
    `https://api.workos.com/_widgets/UserManagement/organizations`,
  ] as const;
};

export const getOrganizationsQueryOptions = <
  TData = Awaited<ReturnType<typeof organizations>>,
  TError = Organizations403 | Organizations404,
>(options?: {
  query?: Partial<
    UseQueryOptions<Awaited<ReturnType<typeof organizations>>, TError, TData>
  >;
  fetch?: RequestInit;
}) => {
  const { query: queryOptions, fetch: fetchOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getOrganizationsQueryKey();

  const queryFn: QueryFunction<Awaited<ReturnType<typeof organizations>>> = ({
    signal,
  }) => organizations({ signal, ...fetchOptions });

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof organizations>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type OrganizationsQueryResult = NonNullable<
  Awaited<ReturnType<typeof organizations>>
>;
export type OrganizationsQueryError = Organizations403 | Organizations404;

export function useOrganizations<
  TData = Awaited<ReturnType<typeof organizations>>,
  TError = Organizations403 | Organizations404,
>(
  options: {
    query: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof organizations>>, TError, TData>
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof organizations>>,
          TError,
          Awaited<ReturnType<typeof organizations>>
        >,
        "initialData"
      >;
    fetch?: RequestInit;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useOrganizations<
  TData = Awaited<ReturnType<typeof organizations>>,
  TError = Organizations403 | Organizations404,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof organizations>>, TError, TData>
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof organizations>>,
          TError,
          Awaited<ReturnType<typeof organizations>>
        >,
        "initialData"
      >;
    fetch?: RequestInit;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useOrganizations<
  TData = Awaited<ReturnType<typeof organizations>>,
  TError = Organizations403 | Organizations404,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof organizations>>, TError, TData>
    >;
    fetch?: RequestInit;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};

export function useOrganizations<
  TData = Awaited<ReturnType<typeof organizations>>,
  TError = Organizations403 | Organizations404,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof organizations>>, TError, TData>
    >;
    fetch?: RequestInit;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions = getOrganizationsQueryOptions(options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData, TError> };

  return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * Returns the list of roles available in the organization that can be assigned to members.
 */
export type rolesResponse200 = {
  data: MemberRole[];
  status: 200;
};

export type rolesResponse403 = {
  data: Roles403;
  status: 403;
};

export type rolesResponse404 = {
  data: Roles404;
  status: 404;
};

export type rolesResponseSuccess = rolesResponse200 & {
  headers: Headers;
};
export type rolesResponseError = (rolesResponse403 | rolesResponse404) & {
  headers: Headers;
};

export type rolesResponse = rolesResponseSuccess | rolesResponseError;

export const getRolesUrl = () => {
  return `https://api.workos.com/_widgets/UserManagement/roles`;
};

export const roles = async (options?: RequestInit): Promise<rolesResponse> => {
  const res = await fetch(getRolesUrl(), {
    ...options,
    method: "GET",
  });

  const body = [204, 205, 304].includes(res.status) ? null : await res.text();

  const data: rolesResponse["data"] = body ? JSON.parse(body) : {};
  return { data, status: res.status, headers: res.headers } as rolesResponse;
};

export const getRolesQueryKey = () => {
  return [`https://api.workos.com/_widgets/UserManagement/roles`] as const;
};

export const getRolesQueryOptions = <
  TData = Awaited<ReturnType<typeof roles>>,
  TError = Roles403 | Roles404,
>(options?: {
  query?: Partial<
    UseQueryOptions<Awaited<ReturnType<typeof roles>>, TError, TData>
  >;
  fetch?: RequestInit;
}) => {
  const { query: queryOptions, fetch: fetchOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getRolesQueryKey();

  const queryFn: QueryFunction<Awaited<ReturnType<typeof roles>>> = ({
    signal,
  }) => roles({ signal, ...fetchOptions });

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof roles>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type RolesQueryResult = NonNullable<Awaited<ReturnType<typeof roles>>>;
export type RolesQueryError = Roles403 | Roles404;

export function useRoles<
  TData = Awaited<ReturnType<typeof roles>>,
  TError = Roles403 | Roles404,
>(
  options: {
    query: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof roles>>, TError, TData>
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof roles>>,
          TError,
          Awaited<ReturnType<typeof roles>>
        >,
        "initialData"
      >;
    fetch?: RequestInit;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useRoles<
  TData = Awaited<ReturnType<typeof roles>>,
  TError = Roles403 | Roles404,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof roles>>, TError, TData>
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof roles>>,
          TError,
          Awaited<ReturnType<typeof roles>>
        >,
        "initialData"
      >;
    fetch?: RequestInit;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useRoles<
  TData = Awaited<ReturnType<typeof roles>>,
  TError = Roles403 | Roles404,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof roles>>, TError, TData>
    >;
    fetch?: RequestInit;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};

export function useRoles<
  TData = Awaited<ReturnType<typeof roles>>,
  TError = Roles403 | Roles404,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof roles>>, TError, TData>
    >;
    fetch?: RequestInit;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions = getRolesQueryOptions(options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData, TError> };

  return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * Returns the list of roles available in the organization along with the user management configuration, such as whether role assignment is enabled.
 */
export type rolesAndConfigResponse200 = {
  data: RolesAndConfigResponse;
  status: 200;
};

export type rolesAndConfigResponse403 = {
  data: RolesAndConfig403;
  status: 403;
};

export type rolesAndConfigResponse404 = {
  data: RolesAndConfig404;
  status: 404;
};

export type rolesAndConfigResponseSuccess = rolesAndConfigResponse200 & {
  headers: Headers;
};
export type rolesAndConfigResponseError = (
  | rolesAndConfigResponse403
  | rolesAndConfigResponse404
) & {
  headers: Headers;
};

export type rolesAndConfigResponse =
  | rolesAndConfigResponseSuccess
  | rolesAndConfigResponseError;

export const getRolesAndConfigUrl = () => {
  return `https://api.workos.com/_widgets/UserManagement/roles-and-config`;
};

export const rolesAndConfig = async (
  options?: RequestInit,
): Promise<rolesAndConfigResponse> => {
  const res = await fetch(getRolesAndConfigUrl(), {
    ...options,
    method: "GET",
  });

  const body = [204, 205, 304].includes(res.status) ? null : await res.text();

  const data: rolesAndConfigResponse["data"] = body ? JSON.parse(body) : {};
  return {
    data,
    status: res.status,
    headers: res.headers,
  } as rolesAndConfigResponse;
};

export const getRolesAndConfigQueryKey = () => {
  return [
    `https://api.workos.com/_widgets/UserManagement/roles-and-config`,
  ] as const;
};

export const getRolesAndConfigQueryOptions = <
  TData = Awaited<ReturnType<typeof rolesAndConfig>>,
  TError = RolesAndConfig403 | RolesAndConfig404,
>(options?: {
  query?: Partial<
    UseQueryOptions<Awaited<ReturnType<typeof rolesAndConfig>>, TError, TData>
  >;
  fetch?: RequestInit;
}) => {
  const { query: queryOptions, fetch: fetchOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getRolesAndConfigQueryKey();

  const queryFn: QueryFunction<Awaited<ReturnType<typeof rolesAndConfig>>> = ({
    signal,
  }) => rolesAndConfig({ signal, ...fetchOptions });

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof rolesAndConfig>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type RolesAndConfigQueryResult = NonNullable<
  Awaited<ReturnType<typeof rolesAndConfig>>
>;
export type RolesAndConfigQueryError = RolesAndConfig403 | RolesAndConfig404;

export function useRolesAndConfig<
  TData = Awaited<ReturnType<typeof rolesAndConfig>>,
  TError = RolesAndConfig403 | RolesAndConfig404,
>(
  options: {
    query: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof rolesAndConfig>>, TError, TData>
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof rolesAndConfig>>,
          TError,
          Awaited<ReturnType<typeof rolesAndConfig>>
        >,
        "initialData"
      >;
    fetch?: RequestInit;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useRolesAndConfig<
  TData = Awaited<ReturnType<typeof rolesAndConfig>>,
  TError = RolesAndConfig403 | RolesAndConfig404,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof rolesAndConfig>>, TError, TData>
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof rolesAndConfig>>,
          TError,
          Awaited<ReturnType<typeof rolesAndConfig>>
        >,
        "initialData"
      >;
    fetch?: RequestInit;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useRolesAndConfig<
  TData = Awaited<ReturnType<typeof rolesAndConfig>>,
  TError = RolesAndConfig403 | RolesAndConfig404,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof rolesAndConfig>>, TError, TData>
    >;
    fetch?: RequestInit;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};

export function useRolesAndConfig<
  TData = Awaited<ReturnType<typeof rolesAndConfig>>,
  TError = RolesAndConfig403 | RolesAndConfig404,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof rolesAndConfig>>, TError, TData>
    >;
    fetch?: RequestInit;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions = getRolesAndConfigQueryOptions(options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData, TError> };

  return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * Returns the authentication methods and MFA factors configured for the authenticated user, including enrolled TOTP factors and passkeys.
 */
export type authenticationInformationResponse200 = {
  data: AuthenticationInformationResponse;
  status: 200;
};

export type authenticationInformationResponse403 = {
  data: AuthenticationInformation403;
  status: 403;
};

export type authenticationInformationResponseSuccess =
  authenticationInformationResponse200 & {
    headers: Headers;
  };
export type authenticationInformationResponseError =
  authenticationInformationResponse403 & {
    headers: Headers;
  };

export type authenticationInformationResponse =
  | authenticationInformationResponseSuccess
  | authenticationInformationResponseError;

export const getAuthenticationInformationUrl = () => {
  return `https://api.workos.com/_widgets/UserProfile/authentication-information`;
};

export const authenticationInformation = async (
  options?: RequestInit,
): Promise<authenticationInformationResponse> => {
  const res = await fetch(getAuthenticationInformationUrl(), {
    ...options,
    method: "GET",
  });

  const body = [204, 205, 304].includes(res.status) ? null : await res.text();

  const data: authenticationInformationResponse["data"] = body
    ? JSON.parse(body)
    : {};
  return {
    data,
    status: res.status,
    headers: res.headers,
  } as authenticationInformationResponse;
};

export const getAuthenticationInformationQueryKey = () => {
  return [
    `https://api.workos.com/_widgets/UserProfile/authentication-information`,
  ] as const;
};

export const getAuthenticationInformationQueryOptions = <
  TData = Awaited<ReturnType<typeof authenticationInformation>>,
  TError = AuthenticationInformation403,
>(options?: {
  query?: Partial<
    UseQueryOptions<
      Awaited<ReturnType<typeof authenticationInformation>>,
      TError,
      TData
    >
  >;
  fetch?: RequestInit;
}) => {
  const { query: queryOptions, fetch: fetchOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getAuthenticationInformationQueryKey();

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof authenticationInformation>>
  > = ({ signal }) => authenticationInformation({ signal, ...fetchOptions });

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof authenticationInformation>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type AuthenticationInformationQueryResult = NonNullable<
  Awaited<ReturnType<typeof authenticationInformation>>
>;
export type AuthenticationInformationQueryError = AuthenticationInformation403;

export function useAuthenticationInformation<
  TData = Awaited<ReturnType<typeof authenticationInformation>>,
  TError = AuthenticationInformation403,
>(
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof authenticationInformation>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof authenticationInformation>>,
          TError,
          Awaited<ReturnType<typeof authenticationInformation>>
        >,
        "initialData"
      >;
    fetch?: RequestInit;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useAuthenticationInformation<
  TData = Awaited<ReturnType<typeof authenticationInformation>>,
  TError = AuthenticationInformation403,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof authenticationInformation>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof authenticationInformation>>,
          TError,
          Awaited<ReturnType<typeof authenticationInformation>>
        >,
        "initialData"
      >;
    fetch?: RequestInit;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useAuthenticationInformation<
  TData = Awaited<ReturnType<typeof authenticationInformation>>,
  TError = AuthenticationInformation403,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof authenticationInformation>>,
        TError,
        TData
      >
    >;
    fetch?: RequestInit;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};

export function useAuthenticationInformation<
  TData = Awaited<ReturnType<typeof authenticationInformation>>,
  TError = AuthenticationInformation403,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof authenticationInformation>>,
        TError,
        TData
      >
    >;
    fetch?: RequestInit;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions = getAuthenticationInformationQueryOptions(options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData, TError> };

  return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * Sets a password for the authenticated user. Only available when the user does not already have a password configured. Requires elevated access.
 */
export type createPasswordResponse201 = {
  data: CreatePassword201;
  status: 201;
};

export type createPasswordResponse400 = {
  data: CreatePassword400;
  status: 400;
};

export type createPasswordResponse403 = {
  data: CreatePassword403;
  status: 403;
};

export type createPasswordResponseSuccess = createPasswordResponse201 & {
  headers: Headers;
};
export type createPasswordResponseError = (
  | createPasswordResponse400
  | createPasswordResponse403
) & {
  headers: Headers;
};

export type createPasswordResponse =
  | createPasswordResponseSuccess
  | createPasswordResponseError;

export const getCreatePasswordUrl = () => {
  return `https://api.workos.com/_widgets/UserProfile/create-password`;
};

export const createPassword = async (
  createPasswordRequest: CreatePasswordRequest,
  options?: RequestInit,
): Promise<createPasswordResponse> => {
  const res = await fetch(getCreatePasswordUrl(), {
    ...options,
    method: "POST",
    headers: { "Content-Type": "application/json", ...options?.headers },
    body: JSON.stringify(createPasswordRequest),
  });

  const body = [204, 205, 304].includes(res.status) ? null : await res.text();

  const data: createPasswordResponse["data"] = body ? JSON.parse(body) : {};
  return {
    data,
    status: res.status,
    headers: res.headers,
  } as createPasswordResponse;
};

export const getCreatePasswordMutationOptions = <
  TError = CreatePassword400 | CreatePassword403,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof createPassword>>,
    TError,
    { data: CreatePasswordRequest },
    TContext
  >;
  fetch?: RequestInit;
}): UseMutationOptions<
  Awaited<ReturnType<typeof createPassword>>,
  TError,
  { data: CreatePasswordRequest },
  TContext
> => {
  const mutationKey = ["createPassword"];
  const { mutation: mutationOptions, fetch: fetchOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, fetch: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof createPassword>>,
    { data: CreatePasswordRequest }
  > = (props) => {
    const { data } = props ?? {};

    return createPassword(data, fetchOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type CreatePasswordMutationResult = NonNullable<
  Awaited<ReturnType<typeof createPassword>>
>;
export type CreatePasswordMutationBody = CreatePasswordRequest;
export type CreatePasswordMutationError = CreatePassword400 | CreatePassword403;

export const useCreatePassword = <
  TError = CreatePassword400 | CreatePassword403,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof createPassword>>,
      TError,
      { data: CreatePasswordRequest },
      TContext
    >;
    fetch?: RequestInit;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof createPassword>>,
  TError,
  { data: CreatePasswordRequest },
  TContext
> => {
  return useMutation(getCreatePasswordMutationOptions(options), queryClient);
};

/**
 * Initiates TOTP (authenticator app) enrollment for the authenticated user by generating a new TOTP secret and QR code. Requires elevated access.
 */
export type createTotpFactorResponse201 = {
  data: CreateTotpFactorResponse;
  status: 201;
};

export type createTotpFactorResponse400 = {
  data: CreateTotpFactor400;
  status: 400;
};

export type createTotpFactorResponse403 = {
  data: CreateTotpFactor403;
  status: 403;
};

export type createTotpFactorResponseSuccess = createTotpFactorResponse201 & {
  headers: Headers;
};
export type createTotpFactorResponseError = (
  | createTotpFactorResponse400
  | createTotpFactorResponse403
) & {
  headers: Headers;
};

export type createTotpFactorResponse =
  | createTotpFactorResponseSuccess
  | createTotpFactorResponseError;

export const getCreateTotpFactorUrl = () => {
  return `https://api.workos.com/_widgets/UserProfile/create-totp-factor`;
};

export const createTotpFactor = async (
  options?: RequestInit,
): Promise<createTotpFactorResponse> => {
  const res = await fetch(getCreateTotpFactorUrl(), {
    ...options,
    method: "POST",
  });

  const body = [204, 205, 304].includes(res.status) ? null : await res.text();

  const data: createTotpFactorResponse["data"] = body ? JSON.parse(body) : {};
  return {
    data,
    status: res.status,
    headers: res.headers,
  } as createTotpFactorResponse;
};

export const getCreateTotpFactorMutationOptions = <
  TError = CreateTotpFactor400 | CreateTotpFactor403,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof createTotpFactor>>,
    TError,
    void,
    TContext
  >;
  fetch?: RequestInit;
}): UseMutationOptions<
  Awaited<ReturnType<typeof createTotpFactor>>,
  TError,
  void,
  TContext
> => {
  const mutationKey = ["createTotpFactor"];
  const { mutation: mutationOptions, fetch: fetchOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, fetch: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof createTotpFactor>>,
    void
  > = () => {
    return createTotpFactor(fetchOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type CreateTotpFactorMutationResult = NonNullable<
  Awaited<ReturnType<typeof createTotpFactor>>
>;

export type CreateTotpFactorMutationError =
  | CreateTotpFactor400
  | CreateTotpFactor403;

export const useCreateTotpFactor = <
  TError = CreateTotpFactor400 | CreateTotpFactor403,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof createTotpFactor>>,
      TError,
      void,
      TContext
    >;
    fetch?: RequestInit;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof createTotpFactor>>,
  TError,
  void,
  TContext
> => {
  return useMutation(getCreateTotpFactorMutationOptions(options), queryClient);
};

/**
 * Returns the profile information of the currently authenticated user, including their name, email, and linked authentication factors.
 */
export type meResponse200 = {
  data: Me;
  status: 200;
};

export type meResponse403 = {
  data: Me403;
  status: 403;
};

export type meResponseSuccess = meResponse200 & {
  headers: Headers;
};
export type meResponseError = meResponse403 & {
  headers: Headers;
};

export type meResponse = meResponseSuccess | meResponseError;

export const getMeUrl = () => {
  return `https://api.workos.com/_widgets/UserProfile/me`;
};

export const me = async (options?: RequestInit): Promise<meResponse> => {
  const res = await fetch(getMeUrl(), {
    ...options,
    method: "GET",
  });

  const body = [204, 205, 304].includes(res.status) ? null : await res.text();

  const data: meResponse["data"] = body ? JSON.parse(body) : {};
  return { data, status: res.status, headers: res.headers } as meResponse;
};

export const getMeQueryKey = () => {
  return [`https://api.workos.com/_widgets/UserProfile/me`] as const;
};

export const getMeQueryOptions = <
  TData = Awaited<ReturnType<typeof me>>,
  TError = Me403,
>(options?: {
  query?: Partial<
    UseQueryOptions<Awaited<ReturnType<typeof me>>, TError, TData>
  >;
  fetch?: RequestInit;
}) => {
  const { query: queryOptions, fetch: fetchOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getMeQueryKey();

  const queryFn: QueryFunction<Awaited<ReturnType<typeof me>>> = ({ signal }) =>
    me({ signal, ...fetchOptions });

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof me>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type MeQueryResult = NonNullable<Awaited<ReturnType<typeof me>>>;
export type MeQueryError = Me403;

export function useMe<TData = Awaited<ReturnType<typeof me>>, TError = Me403>(
  options: {
    query: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof me>>, TError, TData>
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof me>>,
          TError,
          Awaited<ReturnType<typeof me>>
        >,
        "initialData"
      >;
    fetch?: RequestInit;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useMe<TData = Awaited<ReturnType<typeof me>>, TError = Me403>(
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof me>>, TError, TData>
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof me>>,
          TError,
          Awaited<ReturnType<typeof me>>
        >,
        "initialData"
      >;
    fetch?: RequestInit;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useMe<TData = Awaited<ReturnType<typeof me>>, TError = Me403>(
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof me>>, TError, TData>
    >;
    fetch?: RequestInit;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};

export function useMe<TData = Awaited<ReturnType<typeof me>>, TError = Me403>(
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof me>>, TError, TData>
    >;
    fetch?: RequestInit;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions = getMeQueryOptions(options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData, TError> };

  return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * Updates the profile information of the currently authenticated user, such as their first name, last name, or email address.
 */
export type updateMeResponse200 = {
  data: Me;
  status: 200;
};

export type updateMeResponse400 = {
  data: UpdateMe400;
  status: 400;
};

export type updateMeResponse403 = {
  data: UpdateMe403;
  status: 403;
};

export type updateMeResponseSuccess = updateMeResponse200 & {
  headers: Headers;
};
export type updateMeResponseError = (
  | updateMeResponse400
  | updateMeResponse403
) & {
  headers: Headers;
};

export type updateMeResponse = updateMeResponseSuccess | updateMeResponseError;

export const getUpdateMeUrl = () => {
  return `https://api.workos.com/_widgets/UserProfile/me`;
};

export const updateMe = async (
  updateMeBody: UpdateMeBody,
  options?: RequestInit,
): Promise<updateMeResponse> => {
  const res = await fetch(getUpdateMeUrl(), {
    ...options,
    method: "POST",
    headers: { "Content-Type": "application/json", ...options?.headers },
    body: JSON.stringify(updateMeBody),
  });

  const body = [204, 205, 304].includes(res.status) ? null : await res.text();

  const data: updateMeResponse["data"] = body ? JSON.parse(body) : {};
  return { data, status: res.status, headers: res.headers } as updateMeResponse;
};

export const getUpdateMeMutationOptions = <
  TError = UpdateMe400 | UpdateMe403,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof updateMe>>,
    TError,
    { data: UpdateMeBody },
    TContext
  >;
  fetch?: RequestInit;
}): UseMutationOptions<
  Awaited<ReturnType<typeof updateMe>>,
  TError,
  { data: UpdateMeBody },
  TContext
> => {
  const mutationKey = ["updateMe"];
  const { mutation: mutationOptions, fetch: fetchOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, fetch: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof updateMe>>,
    { data: UpdateMeBody }
  > = (props) => {
    const { data } = props ?? {};

    return updateMe(data, fetchOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type UpdateMeMutationResult = NonNullable<
  Awaited<ReturnType<typeof updateMe>>
>;
export type UpdateMeMutationBody = UpdateMeBody;
export type UpdateMeMutationError = UpdateMe400 | UpdateMe403;

export const useUpdateMe = <
  TError = UpdateMe400 | UpdateMe403,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof updateMe>>,
      TError,
      { data: UpdateMeBody },
      TContext
    >;
    fetch?: RequestInit;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof updateMe>>,
  TError,
  { data: UpdateMeBody },
  TContext
> => {
  return useMutation(getUpdateMeMutationOptions(options), queryClient);
};

/**
 * Initiates passkey (WebAuthn) registration for the authenticated user by returning the credential creation options. Requires elevated access.
 */
export type registerPasskeyResponse201 = {
  data: RegisterPasskeyResponse;
  status: 201;
};

export type registerPasskeyResponse400 = {
  data: RegisterPasskey400;
  status: 400;
};

export type registerPasskeyResponse403 = {
  data: RegisterPasskey403;
  status: 403;
};

export type registerPasskeyResponseSuccess = registerPasskeyResponse201 & {
  headers: Headers;
};
export type registerPasskeyResponseError = (
  | registerPasskeyResponse400
  | registerPasskeyResponse403
) & {
  headers: Headers;
};

export type registerPasskeyResponse =
  | registerPasskeyResponseSuccess
  | registerPasskeyResponseError;

export const getRegisterPasskeyUrl = () => {
  return `https://api.workos.com/_widgets/UserProfile/passkeys`;
};

export const registerPasskey = async (
  options?: RequestInit,
): Promise<registerPasskeyResponse> => {
  const res = await fetch(getRegisterPasskeyUrl(), {
    ...options,
    method: "POST",
  });

  const body = [204, 205, 304].includes(res.status) ? null : await res.text();

  const data: registerPasskeyResponse["data"] = body ? JSON.parse(body) : {};
  return {
    data,
    status: res.status,
    headers: res.headers,
  } as registerPasskeyResponse;
};

export const getRegisterPasskeyMutationOptions = <
  TError = RegisterPasskey400 | RegisterPasskey403,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof registerPasskey>>,
    TError,
    void,
    TContext
  >;
  fetch?: RequestInit;
}): UseMutationOptions<
  Awaited<ReturnType<typeof registerPasskey>>,
  TError,
  void,
  TContext
> => {
  const mutationKey = ["registerPasskey"];
  const { mutation: mutationOptions, fetch: fetchOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, fetch: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof registerPasskey>>,
    void
  > = () => {
    return registerPasskey(fetchOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type RegisterPasskeyMutationResult = NonNullable<
  Awaited<ReturnType<typeof registerPasskey>>
>;

export type RegisterPasskeyMutationError =
  | RegisterPasskey400
  | RegisterPasskey403;

export const useRegisterPasskey = <
  TError = RegisterPasskey400 | RegisterPasskey403,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof registerPasskey>>,
      TError,
      void,
      TContext
    >;
    fetch?: RequestInit;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof registerPasskey>>,
  TError,
  void,
  TContext
> => {
  return useMutation(getRegisterPasskeyMutationOptions(options), queryClient);
};

/**
 * Completes passkey (WebAuthn) registration by verifying the credential created by the authenticator. Requires elevated access.
 */
export type verifyPasskeyResponse200 = {
  data: VerifyPasskey200;
  status: 200;
};

export type verifyPasskeyResponse400 = {
  data: VerifyPasskey400;
  status: 400;
};

export type verifyPasskeyResponse403 = {
  data: VerifyPasskey403;
  status: 403;
};

export type verifyPasskeyResponseSuccess = verifyPasskeyResponse200 & {
  headers: Headers;
};
export type verifyPasskeyResponseError = (
  | verifyPasskeyResponse400
  | verifyPasskeyResponse403
) & {
  headers: Headers;
};

export type verifyPasskeyResponse =
  | verifyPasskeyResponseSuccess
  | verifyPasskeyResponseError;

export const getVerifyPasskeyUrl = () => {
  return `https://api.workos.com/_widgets/UserProfile/passkeys/verify`;
};

export const verifyPasskey = async (
  verifyPasskeyRequest: VerifyPasskeyRequest,
  options?: RequestInit,
): Promise<verifyPasskeyResponse> => {
  const res = await fetch(getVerifyPasskeyUrl(), {
    ...options,
    method: "POST",
    headers: { "Content-Type": "application/json", ...options?.headers },
    body: JSON.stringify(verifyPasskeyRequest),
  });

  const body = [204, 205, 304].includes(res.status) ? null : await res.text();

  const data: verifyPasskeyResponse["data"] = body ? JSON.parse(body) : {};
  return {
    data,
    status: res.status,
    headers: res.headers,
  } as verifyPasskeyResponse;
};

export const getVerifyPasskeyMutationOptions = <
  TError = VerifyPasskey400 | VerifyPasskey403,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof verifyPasskey>>,
    TError,
    { data: VerifyPasskeyRequest },
    TContext
  >;
  fetch?: RequestInit;
}): UseMutationOptions<
  Awaited<ReturnType<typeof verifyPasskey>>,
  TError,
  { data: VerifyPasskeyRequest },
  TContext
> => {
  const mutationKey = ["verifyPasskey"];
  const { mutation: mutationOptions, fetch: fetchOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, fetch: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof verifyPasskey>>,
    { data: VerifyPasskeyRequest }
  > = (props) => {
    const { data } = props ?? {};

    return verifyPasskey(data, fetchOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type VerifyPasskeyMutationResult = NonNullable<
  Awaited<ReturnType<typeof verifyPasskey>>
>;
export type VerifyPasskeyMutationBody = VerifyPasskeyRequest;
export type VerifyPasskeyMutationError = VerifyPasskey400 | VerifyPasskey403;

export const useVerifyPasskey = <
  TError = VerifyPasskey400 | VerifyPasskey403,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof verifyPasskey>>,
      TError,
      { data: VerifyPasskeyRequest },
      TContext
    >;
    fetch?: RequestInit;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof verifyPasskey>>,
  TError,
  { data: VerifyPasskeyRequest },
  TContext
> => {
  return useMutation(getVerifyPasskeyMutationOptions(options), queryClient);
};

/**
 * Removes the specified passkey from the authenticated user's account. Requires elevated access.
 */
export type deletePasskeyResponse200 = {
  data: DeletePasskey200;
  status: 200;
};

export type deletePasskeyResponse400 = {
  data: DeletePasskey400;
  status: 400;
};

export type deletePasskeyResponse403 = {
  data: DeletePasskey403;
  status: 403;
};

export type deletePasskeyResponseSuccess = deletePasskeyResponse200 & {
  headers: Headers;
};
export type deletePasskeyResponseError = (
  | deletePasskeyResponse400
  | deletePasskeyResponse403
) & {
  headers: Headers;
};

export type deletePasskeyResponse =
  | deletePasskeyResponseSuccess
  | deletePasskeyResponseError;

export const getDeletePasskeyUrl = (passkeyId: string) => {
  return `https://api.workos.com/_widgets/UserProfile/passkeys/${passkeyId}`;
};

export const deletePasskey = async (
  passkeyId: string,
  options?: RequestInit,
): Promise<deletePasskeyResponse> => {
  const res = await fetch(getDeletePasskeyUrl(passkeyId), {
    ...options,
    method: "DELETE",
  });

  const body = [204, 205, 304].includes(res.status) ? null : await res.text();

  const data: deletePasskeyResponse["data"] = body ? JSON.parse(body) : {};
  return {
    data,
    status: res.status,
    headers: res.headers,
  } as deletePasskeyResponse;
};

export const getDeletePasskeyMutationOptions = <
  TError = DeletePasskey400 | DeletePasskey403,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof deletePasskey>>,
    TError,
    { passkeyId: string },
    TContext
  >;
  fetch?: RequestInit;
}): UseMutationOptions<
  Awaited<ReturnType<typeof deletePasskey>>,
  TError,
  { passkeyId: string },
  TContext
> => {
  const mutationKey = ["deletePasskey"];
  const { mutation: mutationOptions, fetch: fetchOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, fetch: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof deletePasskey>>,
    { passkeyId: string }
  > = (props) => {
    const { passkeyId } = props ?? {};

    return deletePasskey(passkeyId, fetchOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type DeletePasskeyMutationResult = NonNullable<
  Awaited<ReturnType<typeof deletePasskey>>
>;

export type DeletePasskeyMutationError = DeletePasskey400 | DeletePasskey403;

export const useDeletePasskey = <
  TError = DeletePasskey400 | DeletePasskey403,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof deletePasskey>>,
      TError,
      { passkeyId: string },
      TContext
    >;
    fetch?: RequestInit;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof deletePasskey>>,
  TError,
  { passkeyId: string },
  TContext
> => {
  return useMutation(getDeletePasskeyMutationOptions(options), queryClient);
};

/**
 * Sends a verification email containing a one-time code to the new email address. The change does not take effect until the code is submitted via verify-email-change. Requires elevated access.
 */
export type sendEmailChangeResponse200 = {
  data: SendEmailChangeResponse;
  status: 200;
};

export type sendEmailChangeResponse400 = {
  data: SendEmailChange400;
  status: 400;
};

export type sendEmailChangeResponse403 = {
  data: SendEmailChange403;
  status: 403;
};

export type sendEmailChangeResponse409 = {
  data: SendEmailChange409;
  status: 409;
};

export type sendEmailChangeResponse422 = {
  data: SendEmailChange422;
  status: 422;
};

export type sendEmailChangeResponse429 = {
  data: SendEmailChange429;
  status: 429;
};

export type sendEmailChangeResponseSuccess = sendEmailChangeResponse200 & {
  headers: Headers;
};
export type sendEmailChangeResponseError = (
  | sendEmailChangeResponse400
  | sendEmailChangeResponse403
  | sendEmailChangeResponse409
  | sendEmailChangeResponse422
  | sendEmailChangeResponse429
) & {
  headers: Headers;
};

export type sendEmailChangeResponse =
  | sendEmailChangeResponseSuccess
  | sendEmailChangeResponseError;

export const getSendEmailChangeUrl = () => {
  return `https://api.workos.com/_widgets/UserProfile/send-email-change`;
};

export const sendEmailChange = async (
  sendEmailChangeRequest: SendEmailChangeRequest,
  options?: RequestInit,
): Promise<sendEmailChangeResponse> => {
  const res = await fetch(getSendEmailChangeUrl(), {
    ...options,
    method: "POST",
    headers: { "Content-Type": "application/json", ...options?.headers },
    body: JSON.stringify(sendEmailChangeRequest),
  });

  const body = [204, 205, 304].includes(res.status) ? null : await res.text();

  const data: sendEmailChangeResponse["data"] = body ? JSON.parse(body) : {};
  return {
    data,
    status: res.status,
    headers: res.headers,
  } as sendEmailChangeResponse;
};

export const getSendEmailChangeMutationOptions = <
  TError =
    | SendEmailChange400
    | SendEmailChange403
    | SendEmailChange409
    | SendEmailChange422
    | SendEmailChange429,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof sendEmailChange>>,
    TError,
    { data: SendEmailChangeRequest },
    TContext
  >;
  fetch?: RequestInit;
}): UseMutationOptions<
  Awaited<ReturnType<typeof sendEmailChange>>,
  TError,
  { data: SendEmailChangeRequest },
  TContext
> => {
  const mutationKey = ["sendEmailChange"];
  const { mutation: mutationOptions, fetch: fetchOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, fetch: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof sendEmailChange>>,
    { data: SendEmailChangeRequest }
  > = (props) => {
    const { data } = props ?? {};

    return sendEmailChange(data, fetchOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type SendEmailChangeMutationResult = NonNullable<
  Awaited<ReturnType<typeof sendEmailChange>>
>;
export type SendEmailChangeMutationBody = SendEmailChangeRequest;
export type SendEmailChangeMutationError =
  | SendEmailChange400
  | SendEmailChange403
  | SendEmailChange409
  | SendEmailChange422
  | SendEmailChange429;

export const useSendEmailChange = <
  TError =
    | SendEmailChange400
    | SendEmailChange403
    | SendEmailChange409
    | SendEmailChange422
    | SendEmailChange429,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof sendEmailChange>>,
      TError,
      { data: SendEmailChangeRequest },
      TContext
    >;
    fetch?: RequestInit;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof sendEmailChange>>,
  TError,
  { data: SendEmailChangeRequest },
  TContext
> => {
  return useMutation(getSendEmailChangeMutationOptions(options), queryClient);
};

/**
 * Sends a verification email to the authenticated user to confirm their email address.
 */
export type sendVerificationResponse200 = {
  data: SendVerificationResponse;
  status: 200;
};

export type sendVerificationResponse400 = {
  data: SendVerification400;
  status: 400;
};

export type sendVerificationResponse403 = {
  data: SendVerification403;
  status: 403;
};

export type sendVerificationResponseSuccess = sendVerificationResponse200 & {
  headers: Headers;
};
export type sendVerificationResponseError = (
  | sendVerificationResponse400
  | sendVerificationResponse403
) & {
  headers: Headers;
};

export type sendVerificationResponse =
  | sendVerificationResponseSuccess
  | sendVerificationResponseError;

export const getSendVerificationUrl = () => {
  return `https://api.workos.com/_widgets/UserProfile/send-verification`;
};

export const sendVerification = async (
  options?: RequestInit,
): Promise<sendVerificationResponse> => {
  const res = await fetch(getSendVerificationUrl(), {
    ...options,
    method: "POST",
  });

  const body = [204, 205, 304].includes(res.status) ? null : await res.text();

  const data: sendVerificationResponse["data"] = body ? JSON.parse(body) : {};
  return {
    data,
    status: res.status,
    headers: res.headers,
  } as sendVerificationResponse;
};

export const getSendVerificationMutationOptions = <
  TError = SendVerification400 | SendVerification403,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof sendVerification>>,
    TError,
    void,
    TContext
  >;
  fetch?: RequestInit;
}): UseMutationOptions<
  Awaited<ReturnType<typeof sendVerification>>,
  TError,
  void,
  TContext
> => {
  const mutationKey = ["sendVerification"];
  const { mutation: mutationOptions, fetch: fetchOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, fetch: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof sendVerification>>,
    void
  > = () => {
    return sendVerification(fetchOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type SendVerificationMutationResult = NonNullable<
  Awaited<ReturnType<typeof sendVerification>>
>;

export type SendVerificationMutationError =
  | SendVerification400
  | SendVerification403;

export const useSendVerification = <
  TError = SendVerification400 | SendVerification403,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof sendVerification>>,
      TError,
      void,
      TContext
    >;
    fetch?: RequestInit;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof sendVerification>>,
  TError,
  void,
  TContext
> => {
  return useMutation(getSendVerificationMutationOptions(options), queryClient);
};

/**
 * Returns all currently active sessions for the authenticated user, including device and location information where available.
 */
export type sessionsResponse200 = {
  data: ActiveSessionsResponse;
  status: 200;
};

export type sessionsResponse403 = {
  data: Sessions403;
  status: 403;
};

export type sessionsResponseSuccess = sessionsResponse200 & {
  headers: Headers;
};
export type sessionsResponseError = sessionsResponse403 & {
  headers: Headers;
};

export type sessionsResponse = sessionsResponseSuccess | sessionsResponseError;

export const getSessionsUrl = () => {
  return `https://api.workos.com/_widgets/UserProfile/sessions`;
};

export const sessions = async (
  options?: RequestInit,
): Promise<sessionsResponse> => {
  const res = await fetch(getSessionsUrl(), {
    ...options,
    method: "GET",
  });

  const body = [204, 205, 304].includes(res.status) ? null : await res.text();

  const data: sessionsResponse["data"] = body ? JSON.parse(body) : {};
  return { data, status: res.status, headers: res.headers } as sessionsResponse;
};

export const getSessionsQueryKey = () => {
  return [`https://api.workos.com/_widgets/UserProfile/sessions`] as const;
};

export const getSessionsQueryOptions = <
  TData = Awaited<ReturnType<typeof sessions>>,
  TError = Sessions403,
>(options?: {
  query?: Partial<
    UseQueryOptions<Awaited<ReturnType<typeof sessions>>, TError, TData>
  >;
  fetch?: RequestInit;
}) => {
  const { query: queryOptions, fetch: fetchOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getSessionsQueryKey();

  const queryFn: QueryFunction<Awaited<ReturnType<typeof sessions>>> = ({
    signal,
  }) => sessions({ signal, ...fetchOptions });

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof sessions>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type SessionsQueryResult = NonNullable<
  Awaited<ReturnType<typeof sessions>>
>;
export type SessionsQueryError = Sessions403;

export function useSessions<
  TData = Awaited<ReturnType<typeof sessions>>,
  TError = Sessions403,
>(
  options: {
    query: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof sessions>>, TError, TData>
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof sessions>>,
          TError,
          Awaited<ReturnType<typeof sessions>>
        >,
        "initialData"
      >;
    fetch?: RequestInit;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useSessions<
  TData = Awaited<ReturnType<typeof sessions>>,
  TError = Sessions403,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof sessions>>, TError, TData>
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof sessions>>,
          TError,
          Awaited<ReturnType<typeof sessions>>
        >,
        "initialData"
      >;
    fetch?: RequestInit;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useSessions<
  TData = Awaited<ReturnType<typeof sessions>>,
  TError = Sessions403,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof sessions>>, TError, TData>
    >;
    fetch?: RequestInit;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};

export function useSessions<
  TData = Awaited<ReturnType<typeof sessions>>,
  TError = Sessions403,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof sessions>>, TError, TData>
    >;
    fetch?: RequestInit;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions = getSessionsQueryOptions(options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData, TError> };

  return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * Revokes all active sessions for the authenticated user except optionally the current one, signing them out of all other devices.
 */
export type revokeAllSessionsResponse200 = {
  data: RevokeAllSessions200;
  status: 200;
};

export type revokeAllSessionsResponse400 = {
  data: RevokeAllSessions400;
  status: 400;
};

export type revokeAllSessionsResponse403 = {
  data: RevokeAllSessions403;
  status: 403;
};

export type revokeAllSessionsResponseSuccess = revokeAllSessionsResponse200 & {
  headers: Headers;
};
export type revokeAllSessionsResponseError = (
  | revokeAllSessionsResponse400
  | revokeAllSessionsResponse403
) & {
  headers: Headers;
};

export type revokeAllSessionsResponse =
  | revokeAllSessionsResponseSuccess
  | revokeAllSessionsResponseError;

export const getRevokeAllSessionsUrl = () => {
  return `https://api.workos.com/_widgets/UserProfile/sessions/revoke-all`;
};

export const revokeAllSessions = async (
  revokeAllSessionsRequest: RevokeAllSessionsRequest,
  options?: RequestInit,
): Promise<revokeAllSessionsResponse> => {
  const res = await fetch(getRevokeAllSessionsUrl(), {
    ...options,
    method: "DELETE",
    headers: { "Content-Type": "application/json", ...options?.headers },
    body: JSON.stringify(revokeAllSessionsRequest),
  });

  const body = [204, 205, 304].includes(res.status) ? null : await res.text();

  const data: revokeAllSessionsResponse["data"] = body ? JSON.parse(body) : {};
  return {
    data,
    status: res.status,
    headers: res.headers,
  } as revokeAllSessionsResponse;
};

export const getRevokeAllSessionsMutationOptions = <
  TError = RevokeAllSessions400 | RevokeAllSessions403,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof revokeAllSessions>>,
    TError,
    { data: RevokeAllSessionsRequest },
    TContext
  >;
  fetch?: RequestInit;
}): UseMutationOptions<
  Awaited<ReturnType<typeof revokeAllSessions>>,
  TError,
  { data: RevokeAllSessionsRequest },
  TContext
> => {
  const mutationKey = ["revokeAllSessions"];
  const { mutation: mutationOptions, fetch: fetchOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, fetch: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof revokeAllSessions>>,
    { data: RevokeAllSessionsRequest }
  > = (props) => {
    const { data } = props ?? {};

    return revokeAllSessions(data, fetchOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type RevokeAllSessionsMutationResult = NonNullable<
  Awaited<ReturnType<typeof revokeAllSessions>>
>;
export type RevokeAllSessionsMutationBody = RevokeAllSessionsRequest;
export type RevokeAllSessionsMutationError =
  | RevokeAllSessions400
  | RevokeAllSessions403;

export const useRevokeAllSessions = <
  TError = RevokeAllSessions400 | RevokeAllSessions403,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof revokeAllSessions>>,
      TError,
      { data: RevokeAllSessionsRequest },
      TContext
    >;
    fetch?: RequestInit;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof revokeAllSessions>>,
  TError,
  { data: RevokeAllSessionsRequest },
  TContext
> => {
  return useMutation(getRevokeAllSessionsMutationOptions(options), queryClient);
};

/**
 * Revokes a specific active session by ID, signing the user out of that particular device or browser.
 */
export type revokeSessionResponse200 = {
  data: RevokeSession200;
  status: 200;
};

export type revokeSessionResponse400 = {
  data: RevokeSession400;
  status: 400;
};

export type revokeSessionResponse403 = {
  data: RevokeSession403;
  status: 403;
};

export type revokeSessionResponseSuccess = revokeSessionResponse200 & {
  headers: Headers;
};
export type revokeSessionResponseError = (
  | revokeSessionResponse400
  | revokeSessionResponse403
) & {
  headers: Headers;
};

export type revokeSessionResponse =
  | revokeSessionResponseSuccess
  | revokeSessionResponseError;

export const getRevokeSessionUrl = (sessionId: string) => {
  return `https://api.workos.com/_widgets/UserProfile/sessions/revoke/${sessionId}`;
};

export const revokeSession = async (
  sessionId: string,
  options?: RequestInit,
): Promise<revokeSessionResponse> => {
  const res = await fetch(getRevokeSessionUrl(sessionId), {
    ...options,
    method: "DELETE",
  });

  const body = [204, 205, 304].includes(res.status) ? null : await res.text();

  const data: revokeSessionResponse["data"] = body ? JSON.parse(body) : {};
  return {
    data,
    status: res.status,
    headers: res.headers,
  } as revokeSessionResponse;
};

export const getRevokeSessionMutationOptions = <
  TError = RevokeSession400 | RevokeSession403,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof revokeSession>>,
    TError,
    { sessionId: string },
    TContext
  >;
  fetch?: RequestInit;
}): UseMutationOptions<
  Awaited<ReturnType<typeof revokeSession>>,
  TError,
  { sessionId: string },
  TContext
> => {
  const mutationKey = ["revokeSession"];
  const { mutation: mutationOptions, fetch: fetchOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, fetch: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof revokeSession>>,
    { sessionId: string }
  > = (props) => {
    const { sessionId } = props ?? {};

    return revokeSession(sessionId, fetchOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type RevokeSessionMutationResult = NonNullable<
  Awaited<ReturnType<typeof revokeSession>>
>;

export type RevokeSessionMutationError = RevokeSession400 | RevokeSession403;

export const useRevokeSession = <
  TError = RevokeSession400 | RevokeSession403,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof revokeSession>>,
      TError,
      { sessionId: string },
      TContext
    >;
    fetch?: RequestInit;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof revokeSession>>,
  TError,
  { sessionId: string },
  TContext
> => {
  return useMutation(getRevokeSessionMutationOptions(options), queryClient);
};

/**
 * Removes all TOTP factors enrolled for the authenticated user, disabling authenticator app as a second factor. Requires elevated access.
 */
export type deleteTotpFactorsResponse200 = {
  data: DeleteTotpFactors200;
  status: 200;
};

export type deleteTotpFactorsResponse400 = {
  data: DeleteTotpFactors400;
  status: 400;
};

export type deleteTotpFactorsResponse403 = {
  data: DeleteTotpFactors403;
  status: 403;
};

export type deleteTotpFactorsResponseSuccess = deleteTotpFactorsResponse200 & {
  headers: Headers;
};
export type deleteTotpFactorsResponseError = (
  | deleteTotpFactorsResponse400
  | deleteTotpFactorsResponse403
) & {
  headers: Headers;
};

export type deleteTotpFactorsResponse =
  | deleteTotpFactorsResponseSuccess
  | deleteTotpFactorsResponseError;

export const getDeleteTotpFactorsUrl = () => {
  return `https://api.workos.com/_widgets/UserProfile/totp-factors`;
};

export const deleteTotpFactors = async (
  options?: RequestInit,
): Promise<deleteTotpFactorsResponse> => {
  const res = await fetch(getDeleteTotpFactorsUrl(), {
    ...options,
    method: "DELETE",
  });

  const body = [204, 205, 304].includes(res.status) ? null : await res.text();

  const data: deleteTotpFactorsResponse["data"] = body ? JSON.parse(body) : {};
  return {
    data,
    status: res.status,
    headers: res.headers,
  } as deleteTotpFactorsResponse;
};

export const getDeleteTotpFactorsMutationOptions = <
  TError = DeleteTotpFactors400 | DeleteTotpFactors403,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof deleteTotpFactors>>,
    TError,
    void,
    TContext
  >;
  fetch?: RequestInit;
}): UseMutationOptions<
  Awaited<ReturnType<typeof deleteTotpFactors>>,
  TError,
  void,
  TContext
> => {
  const mutationKey = ["deleteTotpFactors"];
  const { mutation: mutationOptions, fetch: fetchOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, fetch: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof deleteTotpFactors>>,
    void
  > = () => {
    return deleteTotpFactors(fetchOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type DeleteTotpFactorsMutationResult = NonNullable<
  Awaited<ReturnType<typeof deleteTotpFactors>>
>;

export type DeleteTotpFactorsMutationError =
  | DeleteTotpFactors400
  | DeleteTotpFactors403;

export const useDeleteTotpFactors = <
  TError = DeleteTotpFactors400 | DeleteTotpFactors403,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof deleteTotpFactors>>,
      TError,
      void,
      TContext
    >;
    fetch?: RequestInit;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof deleteTotpFactors>>,
  TError,
  void,
  TContext
> => {
  return useMutation(getDeleteTotpFactorsMutationOptions(options), queryClient);
};

/**
 * Changes the password for the authenticated user. Requires the current password to be supplied alongside the new password.
 */
export type updatePasswordResponse201 = {
  data: UpdatePassword201;
  status: 201;
};

export type updatePasswordResponse400 = {
  data: UpdatePassword400;
  status: 400;
};

export type updatePasswordResponse403 = {
  data: UpdatePassword403;
  status: 403;
};

export type updatePasswordResponseSuccess = updatePasswordResponse201 & {
  headers: Headers;
};
export type updatePasswordResponseError = (
  | updatePasswordResponse400
  | updatePasswordResponse403
) & {
  headers: Headers;
};

export type updatePasswordResponse =
  | updatePasswordResponseSuccess
  | updatePasswordResponseError;

export const getUpdatePasswordUrl = () => {
  return `https://api.workos.com/_widgets/UserProfile/update-password`;
};

export const updatePassword = async (
  updatePasswordRequest: UpdatePasswordRequest,
  options?: RequestInit,
): Promise<updatePasswordResponse> => {
  const res = await fetch(getUpdatePasswordUrl(), {
    ...options,
    method: "POST",
    headers: { "Content-Type": "application/json", ...options?.headers },
    body: JSON.stringify(updatePasswordRequest),
  });

  const body = [204, 205, 304].includes(res.status) ? null : await res.text();

  const data: updatePasswordResponse["data"] = body ? JSON.parse(body) : {};
  return {
    data,
    status: res.status,
    headers: res.headers,
  } as updatePasswordResponse;
};

export const getUpdatePasswordMutationOptions = <
  TError = UpdatePassword400 | UpdatePassword403,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof updatePassword>>,
    TError,
    { data: UpdatePasswordRequest },
    TContext
  >;
  fetch?: RequestInit;
}): UseMutationOptions<
  Awaited<ReturnType<typeof updatePassword>>,
  TError,
  { data: UpdatePasswordRequest },
  TContext
> => {
  const mutationKey = ["updatePassword"];
  const { mutation: mutationOptions, fetch: fetchOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, fetch: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof updatePassword>>,
    { data: UpdatePasswordRequest }
  > = (props) => {
    const { data } = props ?? {};

    return updatePassword(data, fetchOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type UpdatePasswordMutationResult = NonNullable<
  Awaited<ReturnType<typeof updatePassword>>
>;
export type UpdatePasswordMutationBody = UpdatePasswordRequest;
export type UpdatePasswordMutationError = UpdatePassword400 | UpdatePassword403;

export const useUpdatePassword = <
  TError = UpdatePassword400 | UpdatePassword403,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof updatePassword>>,
      TError,
      { data: UpdatePasswordRequest },
      TContext
    >;
    fetch?: RequestInit;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof updatePassword>>,
  TError,
  { data: UpdatePasswordRequest },
  TContext
> => {
  return useMutation(getUpdatePasswordMutationOptions(options), queryClient);
};

/**
 * Verifies the email address of the authenticated user using the code sent via the send-verification endpoint. On success, returns an elevated access token that grants access to sensitive operations such as MFA enrollment and passkey management.
 */
export type verifyResponse200 = {
  data: VerifyResponse;
  status: 200;
};

export type verifyResponse400 = {
  data: Verify400;
  status: 400;
};

export type verifyResponse403 = {
  data: Verify403;
  status: 403;
};

export type verifyResponseSuccess = verifyResponse200 & {
  headers: Headers;
};
export type verifyResponseError = (verifyResponse400 | verifyResponse403) & {
  headers: Headers;
};

export type verifyResponse = verifyResponseSuccess | verifyResponseError;

export const getVerifyUrl = () => {
  return `https://api.workos.com/_widgets/UserProfile/verify`;
};

export const verify = async (
  verifyRequest: VerifyRequest,
  options?: RequestInit,
): Promise<verifyResponse> => {
  const res = await fetch(getVerifyUrl(), {
    ...options,
    method: "POST",
    headers: { "Content-Type": "application/json", ...options?.headers },
    body: JSON.stringify(verifyRequest),
  });

  const body = [204, 205, 304].includes(res.status) ? null : await res.text();

  const data: verifyResponse["data"] = body ? JSON.parse(body) : {};
  return { data, status: res.status, headers: res.headers } as verifyResponse;
};

export const getVerifyMutationOptions = <
  TError = Verify400 | Verify403,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof verify>>,
    TError,
    { data: VerifyRequest },
    TContext
  >;
  fetch?: RequestInit;
}): UseMutationOptions<
  Awaited<ReturnType<typeof verify>>,
  TError,
  { data: VerifyRequest },
  TContext
> => {
  const mutationKey = ["verify"];
  const { mutation: mutationOptions, fetch: fetchOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, fetch: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof verify>>,
    { data: VerifyRequest }
  > = (props) => {
    const { data } = props ?? {};

    return verify(data, fetchOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type VerifyMutationResult = NonNullable<
  Awaited<ReturnType<typeof verify>>
>;
export type VerifyMutationBody = VerifyRequest;
export type VerifyMutationError = Verify400 | Verify403;

export const useVerify = <TError = Verify400 | Verify403, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof verify>>,
      TError,
      { data: VerifyRequest },
      TContext
    >;
    fetch?: RequestInit;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof verify>>,
  TError,
  { data: VerifyRequest },
  TContext
> => {
  return useMutation(getVerifyMutationOptions(options), queryClient);
};

/**
 * Verifies a pending email change using the one-time code sent via send-email-change. On success, updates the user’s email address and returns the updated profile along with an elevated access token that grants access to sensitive operations such as setting a password.
 */
export type verifyEmailChangeResponse200 = {
  data: VerifyEmailChangeResponse;
  status: 200;
};

export type verifyEmailChangeResponse400 = {
  data: VerifyEmailChange400;
  status: 400;
};

export type verifyEmailChangeResponse403 = {
  data: VerifyEmailChange403;
  status: 403;
};

export type verifyEmailChangeResponse409 = {
  data: VerifyEmailChange409;
  status: 409;
};

export type verifyEmailChangeResponse422 = {
  data: VerifyEmailChange422;
  status: 422;
};

export type verifyEmailChangeResponse429 = {
  data: VerifyEmailChange429;
  status: 429;
};

export type verifyEmailChangeResponseSuccess = verifyEmailChangeResponse200 & {
  headers: Headers;
};
export type verifyEmailChangeResponseError = (
  | verifyEmailChangeResponse400
  | verifyEmailChangeResponse403
  | verifyEmailChangeResponse409
  | verifyEmailChangeResponse422
  | verifyEmailChangeResponse429
) & {
  headers: Headers;
};

export type verifyEmailChangeResponse =
  | verifyEmailChangeResponseSuccess
  | verifyEmailChangeResponseError;

export const getVerifyEmailChangeUrl = () => {
  return `https://api.workos.com/_widgets/UserProfile/verify-email-change`;
};

export const verifyEmailChange = async (
  verifyEmailChangeRequest: VerifyEmailChangeRequest,
  options?: RequestInit,
): Promise<verifyEmailChangeResponse> => {
  const res = await fetch(getVerifyEmailChangeUrl(), {
    ...options,
    method: "POST",
    headers: { "Content-Type": "application/json", ...options?.headers },
    body: JSON.stringify(verifyEmailChangeRequest),
  });

  const body = [204, 205, 304].includes(res.status) ? null : await res.text();

  const data: verifyEmailChangeResponse["data"] = body ? JSON.parse(body) : {};
  return {
    data,
    status: res.status,
    headers: res.headers,
  } as verifyEmailChangeResponse;
};

export const getVerifyEmailChangeMutationOptions = <
  TError =
    | VerifyEmailChange400
    | VerifyEmailChange403
    | VerifyEmailChange409
    | VerifyEmailChange422
    | VerifyEmailChange429,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof verifyEmailChange>>,
    TError,
    { data: VerifyEmailChangeRequest },
    TContext
  >;
  fetch?: RequestInit;
}): UseMutationOptions<
  Awaited<ReturnType<typeof verifyEmailChange>>,
  TError,
  { data: VerifyEmailChangeRequest },
  TContext
> => {
  const mutationKey = ["verifyEmailChange"];
  const { mutation: mutationOptions, fetch: fetchOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, fetch: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof verifyEmailChange>>,
    { data: VerifyEmailChangeRequest }
  > = (props) => {
    const { data } = props ?? {};

    return verifyEmailChange(data, fetchOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type VerifyEmailChangeMutationResult = NonNullable<
  Awaited<ReturnType<typeof verifyEmailChange>>
>;
export type VerifyEmailChangeMutationBody = VerifyEmailChangeRequest;
export type VerifyEmailChangeMutationError =
  | VerifyEmailChange400
  | VerifyEmailChange403
  | VerifyEmailChange409
  | VerifyEmailChange422
  | VerifyEmailChange429;

export const useVerifyEmailChange = <
  TError =
    | VerifyEmailChange400
    | VerifyEmailChange403
    | VerifyEmailChange409
    | VerifyEmailChange422
    | VerifyEmailChange429,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof verifyEmailChange>>,
      TError,
      { data: VerifyEmailChangeRequest },
      TContext
    >;
    fetch?: RequestInit;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof verifyEmailChange>>,
  TError,
  { data: VerifyEmailChangeRequest },
  TContext
> => {
  return useMutation(getVerifyEmailChangeMutationOptions(options), queryClient);
};

/**
 * Completes TOTP enrollment by verifying the one-time code generated by the authenticator app. Requires elevated access.
 */
export type verifyTotpFactorResponse200 = {
  data: VerifyTotpFactor200;
  status: 200;
};

export type verifyTotpFactorResponse400 = {
  data: VerifyTotpFactor400;
  status: 400;
};

export type verifyTotpFactorResponse403 = {
  data: VerifyTotpFactor403;
  status: 403;
};

export type verifyTotpFactorResponseSuccess = verifyTotpFactorResponse200 & {
  headers: Headers;
};
export type verifyTotpFactorResponseError = (
  | verifyTotpFactorResponse400
  | verifyTotpFactorResponse403
) & {
  headers: Headers;
};

export type verifyTotpFactorResponse =
  | verifyTotpFactorResponseSuccess
  | verifyTotpFactorResponseError;

export const getVerifyTotpFactorUrl = () => {
  return `https://api.workos.com/_widgets/UserProfile/verify-totp-factor`;
};

export const verifyTotpFactor = async (
  verifyTotpFactorRequest: VerifyTotpFactorRequest,
  options?: RequestInit,
): Promise<verifyTotpFactorResponse> => {
  const res = await fetch(getVerifyTotpFactorUrl(), {
    ...options,
    method: "POST",
    headers: { "Content-Type": "application/json", ...options?.headers },
    body: JSON.stringify(verifyTotpFactorRequest),
  });

  const body = [204, 205, 304].includes(res.status) ? null : await res.text();

  const data: verifyTotpFactorResponse["data"] = body ? JSON.parse(body) : {};
  return {
    data,
    status: res.status,
    headers: res.headers,
  } as verifyTotpFactorResponse;
};

export const getVerifyTotpFactorMutationOptions = <
  TError = VerifyTotpFactor400 | VerifyTotpFactor403,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof verifyTotpFactor>>,
    TError,
    { data: VerifyTotpFactorRequest },
    TContext
  >;
  fetch?: RequestInit;
}): UseMutationOptions<
  Awaited<ReturnType<typeof verifyTotpFactor>>,
  TError,
  { data: VerifyTotpFactorRequest },
  TContext
> => {
  const mutationKey = ["verifyTotpFactor"];
  const { mutation: mutationOptions, fetch: fetchOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, fetch: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof verifyTotpFactor>>,
    { data: VerifyTotpFactorRequest }
  > = (props) => {
    const { data } = props ?? {};

    return verifyTotpFactor(data, fetchOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type VerifyTotpFactorMutationResult = NonNullable<
  Awaited<ReturnType<typeof verifyTotpFactor>>
>;
export type VerifyTotpFactorMutationBody = VerifyTotpFactorRequest;
export type VerifyTotpFactorMutationError =
  | VerifyTotpFactor400
  | VerifyTotpFactor403;

export const useVerifyTotpFactor = <
  TError = VerifyTotpFactor400 | VerifyTotpFactor403,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof verifyTotpFactor>>,
      TError,
      { data: VerifyTotpFactorRequest },
      TContext
    >;
    fetch?: RequestInit;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof verifyTotpFactor>>,
  TError,
  { data: VerifyTotpFactorRequest },
  TContext
> => {
  return useMutation(getVerifyTotpFactorMutationOptions(options), queryClient);
};

export type getAuditLogStreamResponse200 = {
  data: GetAuditLogStream200;
  status: 200;
};

export type getAuditLogStreamResponse403 = {
  data: GetAuditLogStream403;
  status: 403;
};

export type getAuditLogStreamResponseSuccess = getAuditLogStreamResponse200 & {
  headers: Headers;
};
export type getAuditLogStreamResponseError = getAuditLogStreamResponse403 & {
  headers: Headers;
};

export type getAuditLogStreamResponse =
  | getAuditLogStreamResponseSuccess
  | getAuditLogStreamResponseError;

export const getGetAuditLogStreamUrl = () => {
  return `https://api.workos.com/_widgets/admin-portal/audit-log-stream`;
};

export const getAuditLogStream = async (
  options?: RequestInit,
): Promise<getAuditLogStreamResponse> => {
  const res = await fetch(getGetAuditLogStreamUrl(), {
    ...options,
    method: "GET",
  });

  const body = [204, 205, 304].includes(res.status) ? null : await res.text();

  const data: getAuditLogStreamResponse["data"] = body ? JSON.parse(body) : {};
  return {
    data,
    status: res.status,
    headers: res.headers,
  } as getAuditLogStreamResponse;
};

export const getGetAuditLogStreamQueryKey = () => {
  return [
    `https://api.workos.com/_widgets/admin-portal/audit-log-stream`,
  ] as const;
};

export const getGetAuditLogStreamQueryOptions = <
  TData = Awaited<ReturnType<typeof getAuditLogStream>>,
  TError = GetAuditLogStream403,
>(options?: {
  query?: Partial<
    UseQueryOptions<
      Awaited<ReturnType<typeof getAuditLogStream>>,
      TError,
      TData
    >
  >;
  fetch?: RequestInit;
}) => {
  const { query: queryOptions, fetch: fetchOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetAuditLogStreamQueryKey();

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getAuditLogStream>>
  > = ({ signal }) => getAuditLogStream({ signal, ...fetchOptions });

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getAuditLogStream>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetAuditLogStreamQueryResult = NonNullable<
  Awaited<ReturnType<typeof getAuditLogStream>>
>;
export type GetAuditLogStreamQueryError = GetAuditLogStream403;

export function useGetAuditLogStream<
  TData = Awaited<ReturnType<typeof getAuditLogStream>>,
  TError = GetAuditLogStream403,
>(
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getAuditLogStream>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getAuditLogStream>>,
          TError,
          Awaited<ReturnType<typeof getAuditLogStream>>
        >,
        "initialData"
      >;
    fetch?: RequestInit;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetAuditLogStream<
  TData = Awaited<ReturnType<typeof getAuditLogStream>>,
  TError = GetAuditLogStream403,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getAuditLogStream>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getAuditLogStream>>,
          TError,
          Awaited<ReturnType<typeof getAuditLogStream>>
        >,
        "initialData"
      >;
    fetch?: RequestInit;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetAuditLogStream<
  TData = Awaited<ReturnType<typeof getAuditLogStream>>,
  TError = GetAuditLogStream403,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getAuditLogStream>>,
        TError,
        TData
      >
    >;
    fetch?: RequestInit;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};

export function useGetAuditLogStream<
  TData = Awaited<ReturnType<typeof getAuditLogStream>>,
  TError = GetAuditLogStream403,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getAuditLogStream>>,
        TError,
        TData
      >
    >;
    fetch?: RequestInit;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions = getGetAuditLogStreamQueryOptions(options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData, TError> };

  return { ...query, queryKey: queryOptions.queryKey };
}

export type generateAdminPortalLinkResponse201 = {
  data: GenerateAdminPortalLink201;
  status: 201;
};

export type generateAdminPortalLinkResponse403 = {
  data: GenerateAdminPortalLink403;
  status: 403;
};

export type generateAdminPortalLinkResponse404 = {
  data: GenerateAdminPortalLink404;
  status: 404;
};

export type generateAdminPortalLinkResponse500 = {
  data: GenerateAdminPortalLink500;
  status: 500;
};

export type generateAdminPortalLinkResponseSuccess =
  generateAdminPortalLinkResponse201 & {
    headers: Headers;
  };
export type generateAdminPortalLinkResponseError = (
  | generateAdminPortalLinkResponse403
  | generateAdminPortalLinkResponse404
  | generateAdminPortalLinkResponse500
) & {
  headers: Headers;
};

export type generateAdminPortalLinkResponse =
  | generateAdminPortalLinkResponseSuccess
  | generateAdminPortalLinkResponseError;

export const getGenerateAdminPortalLinkUrl = (
  params: GenerateAdminPortalLinkParams,
) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== undefined) {
      normalizedParams.append(key, value === null ? "null" : value.toString());
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0
    ? `https://api.workos.com/_widgets/admin-portal/generate-link?${stringifiedParams}`
    : `https://api.workos.com/_widgets/admin-portal/generate-link`;
};

export const generateAdminPortalLink = async (
  params: GenerateAdminPortalLinkParams,
  options?: RequestInit,
): Promise<generateAdminPortalLinkResponse> => {
  const res = await fetch(getGenerateAdminPortalLinkUrl(params), {
    ...options,
    method: "POST",
  });

  const body = [204, 205, 304].includes(res.status) ? null : await res.text();

  const data: generateAdminPortalLinkResponse["data"] = body
    ? JSON.parse(body)
    : {};
  return {
    data,
    status: res.status,
    headers: res.headers,
  } as generateAdminPortalLinkResponse;
};

export const getGenerateAdminPortalLinkMutationOptions = <
  TError =
    | GenerateAdminPortalLink403
    | GenerateAdminPortalLink404
    | GenerateAdminPortalLink500,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof generateAdminPortalLink>>,
    TError,
    { params: GenerateAdminPortalLinkParams },
    TContext
  >;
  fetch?: RequestInit;
}): UseMutationOptions<
  Awaited<ReturnType<typeof generateAdminPortalLink>>,
  TError,
  { params: GenerateAdminPortalLinkParams },
  TContext
> => {
  const mutationKey = ["generateAdminPortalLink"];
  const { mutation: mutationOptions, fetch: fetchOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, fetch: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof generateAdminPortalLink>>,
    { params: GenerateAdminPortalLinkParams }
  > = (props) => {
    const { params } = props ?? {};

    return generateAdminPortalLink(params, fetchOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type GenerateAdminPortalLinkMutationResult = NonNullable<
  Awaited<ReturnType<typeof generateAdminPortalLink>>
>;

export type GenerateAdminPortalLinkMutationError =
  | GenerateAdminPortalLink403
  | GenerateAdminPortalLink404
  | GenerateAdminPortalLink500;

export const useGenerateAdminPortalLink = <
  TError =
    | GenerateAdminPortalLink403
    | GenerateAdminPortalLink404
    | GenerateAdminPortalLink500,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof generateAdminPortalLink>>,
      TError,
      { params: GenerateAdminPortalLinkParams },
      TContext
    >;
    fetch?: RequestInit;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof generateAdminPortalLink>>,
  TError,
  { params: GenerateAdminPortalLinkParams },
  TContext
> => {
  return useMutation(
    getGenerateAdminPortalLinkMutationOptions(options),
    queryClient,
  );
};

export type listOrganizationDomainsResponse200 = {
  data: ListOrganizationDomains200;
  status: 200;
};

export type listOrganizationDomainsResponse403 = {
  data: ListOrganizationDomains403;
  status: 403;
};

export type listOrganizationDomainsResponse404 = {
  data: ListOrganizationDomains404;
  status: 404;
};

export type listOrganizationDomainsResponseSuccess =
  listOrganizationDomainsResponse200 & {
    headers: Headers;
  };
export type listOrganizationDomainsResponseError = (
  | listOrganizationDomainsResponse403
  | listOrganizationDomainsResponse404
) & {
  headers: Headers;
};

export type listOrganizationDomainsResponse =
  | listOrganizationDomainsResponseSuccess
  | listOrganizationDomainsResponseError;

export const getListOrganizationDomainsUrl = () => {
  return `https://api.workos.com/_widgets/admin-portal/organization-domains`;
};

export const listOrganizationDomains = async (
  options?: RequestInit,
): Promise<listOrganizationDomainsResponse> => {
  const res = await fetch(getListOrganizationDomainsUrl(), {
    ...options,
    method: "GET",
  });

  const body = [204, 205, 304].includes(res.status) ? null : await res.text();

  const data: listOrganizationDomainsResponse["data"] = body
    ? JSON.parse(body)
    : {};
  return {
    data,
    status: res.status,
    headers: res.headers,
  } as listOrganizationDomainsResponse;
};

export const getListOrganizationDomainsQueryKey = () => {
  return [
    `https://api.workos.com/_widgets/admin-portal/organization-domains`,
  ] as const;
};

export const getListOrganizationDomainsQueryOptions = <
  TData = Awaited<ReturnType<typeof listOrganizationDomains>>,
  TError = ListOrganizationDomains403 | ListOrganizationDomains404,
>(options?: {
  query?: Partial<
    UseQueryOptions<
      Awaited<ReturnType<typeof listOrganizationDomains>>,
      TError,
      TData
    >
  >;
  fetch?: RequestInit;
}) => {
  const { query: queryOptions, fetch: fetchOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getListOrganizationDomainsQueryKey();

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof listOrganizationDomains>>
  > = ({ signal }) => listOrganizationDomains({ signal, ...fetchOptions });

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof listOrganizationDomains>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type ListOrganizationDomainsQueryResult = NonNullable<
  Awaited<ReturnType<typeof listOrganizationDomains>>
>;
export type ListOrganizationDomainsQueryError =
  | ListOrganizationDomains403
  | ListOrganizationDomains404;

export function useListOrganizationDomains<
  TData = Awaited<ReturnType<typeof listOrganizationDomains>>,
  TError = ListOrganizationDomains403 | ListOrganizationDomains404,
>(
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof listOrganizationDomains>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listOrganizationDomains>>,
          TError,
          Awaited<ReturnType<typeof listOrganizationDomains>>
        >,
        "initialData"
      >;
    fetch?: RequestInit;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useListOrganizationDomains<
  TData = Awaited<ReturnType<typeof listOrganizationDomains>>,
  TError = ListOrganizationDomains403 | ListOrganizationDomains404,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof listOrganizationDomains>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listOrganizationDomains>>,
          TError,
          Awaited<ReturnType<typeof listOrganizationDomains>>
        >,
        "initialData"
      >;
    fetch?: RequestInit;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useListOrganizationDomains<
  TData = Awaited<ReturnType<typeof listOrganizationDomains>>,
  TError = ListOrganizationDomains403 | ListOrganizationDomains404,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof listOrganizationDomains>>,
        TError,
        TData
      >
    >;
    fetch?: RequestInit;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};

export function useListOrganizationDomains<
  TData = Awaited<ReturnType<typeof listOrganizationDomains>>,
  TError = ListOrganizationDomains403 | ListOrganizationDomains404,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof listOrganizationDomains>>,
        TError,
        TData
      >
    >;
    fetch?: RequestInit;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions = getListOrganizationDomainsQueryOptions(options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData, TError> };

  return { ...query, queryKey: queryOptions.queryKey };
}

export type deleteOrganizationDomainResponse200 = {
  data: OrganizationDomain;
  status: 200;
};

export type deleteOrganizationDomainResponse403 = {
  data: DeleteOrganizationDomain403;
  status: 403;
};

export type deleteOrganizationDomainResponse404 = {
  data: DeleteOrganizationDomain404;
  status: 404;
};

export type deleteOrganizationDomainResponseSuccess =
  deleteOrganizationDomainResponse200 & {
    headers: Headers;
  };
export type deleteOrganizationDomainResponseError = (
  | deleteOrganizationDomainResponse403
  | deleteOrganizationDomainResponse404
) & {
  headers: Headers;
};

export type deleteOrganizationDomainResponse =
  | deleteOrganizationDomainResponseSuccess
  | deleteOrganizationDomainResponseError;

export const getDeleteOrganizationDomainUrl = (domainId: string) => {
  return `https://api.workos.com/_widgets/admin-portal/organization-domains/${domainId}`;
};

export const deleteOrganizationDomain = async (
  domainId: string,
  options?: RequestInit,
): Promise<deleteOrganizationDomainResponse> => {
  const res = await fetch(getDeleteOrganizationDomainUrl(domainId), {
    ...options,
    method: "DELETE",
  });

  const body = [204, 205, 304].includes(res.status) ? null : await res.text();

  const data: deleteOrganizationDomainResponse["data"] = body
    ? JSON.parse(body)
    : {};
  return {
    data,
    status: res.status,
    headers: res.headers,
  } as deleteOrganizationDomainResponse;
};

export const getDeleteOrganizationDomainMutationOptions = <
  TError = DeleteOrganizationDomain403 | DeleteOrganizationDomain404,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof deleteOrganizationDomain>>,
    TError,
    { domainId: string },
    TContext
  >;
  fetch?: RequestInit;
}): UseMutationOptions<
  Awaited<ReturnType<typeof deleteOrganizationDomain>>,
  TError,
  { domainId: string },
  TContext
> => {
  const mutationKey = ["deleteOrganizationDomain"];
  const { mutation: mutationOptions, fetch: fetchOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, fetch: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof deleteOrganizationDomain>>,
    { domainId: string }
  > = (props) => {
    const { domainId } = props ?? {};

    return deleteOrganizationDomain(domainId, fetchOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type DeleteOrganizationDomainMutationResult = NonNullable<
  Awaited<ReturnType<typeof deleteOrganizationDomain>>
>;

export type DeleteOrganizationDomainMutationError =
  | DeleteOrganizationDomain403
  | DeleteOrganizationDomain404;

export const useDeleteOrganizationDomain = <
  TError = DeleteOrganizationDomain403 | DeleteOrganizationDomain404,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof deleteOrganizationDomain>>,
      TError,
      { domainId: string },
      TContext
    >;
    fetch?: RequestInit;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof deleteOrganizationDomain>>,
  TError,
  { domainId: string },
  TContext
> => {
  return useMutation(
    getDeleteOrganizationDomainMutationOptions(options),
    queryClient,
  );
};

export type reverifyOrganizationDomainResponse200 = {
  data: OrganizationDomain;
  status: 200;
};

export type reverifyOrganizationDomainResponse403 = {
  data: ReverifyOrganizationDomain403;
  status: 403;
};

export type reverifyOrganizationDomainResponse404 = {
  data: ReverifyOrganizationDomain404;
  status: 404;
};

export type reverifyOrganizationDomainResponseSuccess =
  reverifyOrganizationDomainResponse200 & {
    headers: Headers;
  };
export type reverifyOrganizationDomainResponseError = (
  | reverifyOrganizationDomainResponse403
  | reverifyOrganizationDomainResponse404
) & {
  headers: Headers;
};

export type reverifyOrganizationDomainResponse =
  | reverifyOrganizationDomainResponseSuccess
  | reverifyOrganizationDomainResponseError;

export const getReverifyOrganizationDomainUrl = (domainId: string) => {
  return `https://api.workos.com/_widgets/admin-portal/organization-domains/${domainId}/reverify`;
};

export const reverifyOrganizationDomain = async (
  domainId: string,
  options?: RequestInit,
): Promise<reverifyOrganizationDomainResponse> => {
  const res = await fetch(getReverifyOrganizationDomainUrl(domainId), {
    ...options,
    method: "POST",
  });

  const body = [204, 205, 304].includes(res.status) ? null : await res.text();

  const data: reverifyOrganizationDomainResponse["data"] = body
    ? JSON.parse(body)
    : {};
  return {
    data,
    status: res.status,
    headers: res.headers,
  } as reverifyOrganizationDomainResponse;
};

export const getReverifyOrganizationDomainMutationOptions = <
  TError = ReverifyOrganizationDomain403 | ReverifyOrganizationDomain404,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof reverifyOrganizationDomain>>,
    TError,
    { domainId: string },
    TContext
  >;
  fetch?: RequestInit;
}): UseMutationOptions<
  Awaited<ReturnType<typeof reverifyOrganizationDomain>>,
  TError,
  { domainId: string },
  TContext
> => {
  const mutationKey = ["reverifyOrganizationDomain"];
  const { mutation: mutationOptions, fetch: fetchOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, fetch: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof reverifyOrganizationDomain>>,
    { domainId: string }
  > = (props) => {
    const { domainId } = props ?? {};

    return reverifyOrganizationDomain(domainId, fetchOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type ReverifyOrganizationDomainMutationResult = NonNullable<
  Awaited<ReturnType<typeof reverifyOrganizationDomain>>
>;

export type ReverifyOrganizationDomainMutationError =
  | ReverifyOrganizationDomain403
  | ReverifyOrganizationDomain404;

export const useReverifyOrganizationDomain = <
  TError = ReverifyOrganizationDomain403 | ReverifyOrganizationDomain404,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof reverifyOrganizationDomain>>,
      TError,
      { domainId: string },
      TContext
    >;
    fetch?: RequestInit;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof reverifyOrganizationDomain>>,
  TError,
  { domainId: string },
  TContext
> => {
  return useMutation(
    getReverifyOrganizationDomainMutationOptions(options),
    queryClient,
  );
};

export type listSsoConnectionsResponse200 = {
  data: SsoConnection[];
  status: 200;
};

export type listSsoConnectionsResponseSuccess =
  listSsoConnectionsResponse200 & {
    headers: Headers;
  };
export type listSsoConnectionsResponse = listSsoConnectionsResponseSuccess;

export const getListSsoConnectionsUrl = () => {
  return `https://api.workos.com/_widgets/admin-portal/sso-connections`;
};

export const listSsoConnections = async (
  options?: RequestInit,
): Promise<listSsoConnectionsResponse> => {
  const res = await fetch(getListSsoConnectionsUrl(), {
    ...options,
    method: "GET",
  });

  const body = [204, 205, 304].includes(res.status) ? null : await res.text();

  const data: listSsoConnectionsResponse["data"] = body ? JSON.parse(body) : {};
  return {
    data,
    status: res.status,
    headers: res.headers,
  } as listSsoConnectionsResponse;
};

export const getListSsoConnectionsQueryKey = () => {
  return [
    `https://api.workos.com/_widgets/admin-portal/sso-connections`,
  ] as const;
};

export const getListSsoConnectionsQueryOptions = <
  TData = Awaited<ReturnType<typeof listSsoConnections>>,
  TError = unknown,
>(options?: {
  query?: Partial<
    UseQueryOptions<
      Awaited<ReturnType<typeof listSsoConnections>>,
      TError,
      TData
    >
  >;
  fetch?: RequestInit;
}) => {
  const { query: queryOptions, fetch: fetchOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getListSsoConnectionsQueryKey();

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof listSsoConnections>>
  > = ({ signal }) => listSsoConnections({ signal, ...fetchOptions });

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof listSsoConnections>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type ListSsoConnectionsQueryResult = NonNullable<
  Awaited<ReturnType<typeof listSsoConnections>>
>;
export type ListSsoConnectionsQueryError = unknown;

export function useListSsoConnections<
  TData = Awaited<ReturnType<typeof listSsoConnections>>,
  TError = unknown,
>(
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof listSsoConnections>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listSsoConnections>>,
          TError,
          Awaited<ReturnType<typeof listSsoConnections>>
        >,
        "initialData"
      >;
    fetch?: RequestInit;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useListSsoConnections<
  TData = Awaited<ReturnType<typeof listSsoConnections>>,
  TError = unknown,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof listSsoConnections>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listSsoConnections>>,
          TError,
          Awaited<ReturnType<typeof listSsoConnections>>
        >,
        "initialData"
      >;
    fetch?: RequestInit;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useListSsoConnections<
  TData = Awaited<ReturnType<typeof listSsoConnections>>,
  TError = unknown,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof listSsoConnections>>,
        TError,
        TData
      >
    >;
    fetch?: RequestInit;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};

export function useListSsoConnections<
  TData = Awaited<ReturnType<typeof listSsoConnections>>,
  TError = unknown,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof listSsoConnections>>,
        TError,
        TData
      >
    >;
    fetch?: RequestInit;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions = getListSsoConnectionsQueryOptions(options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData, TError> };

  return { ...query, queryKey: queryOptions.queryKey };
}

export type listDirectoriesResponse200 = {
  data: DirectoriesResponse;
  status: 200;
};

export type listDirectoriesResponse403 = {
  data: ListDirectories403;
  status: 403;
};

export type listDirectoriesResponse404 = {
  data: ListDirectories404;
  status: 404;
};

export type listDirectoriesResponseSuccess = listDirectoriesResponse200 & {
  headers: Headers;
};
export type listDirectoriesResponseError = (
  | listDirectoriesResponse403
  | listDirectoriesResponse404
) & {
  headers: Headers;
};

export type listDirectoriesResponse =
  | listDirectoriesResponseSuccess
  | listDirectoriesResponseError;

export const getListDirectoriesUrl = () => {
  return `https://api.workos.com/_widgets/directory-sync/directories`;
};

export const listDirectories = async (
  options?: RequestInit,
): Promise<listDirectoriesResponse> => {
  const res = await fetch(getListDirectoriesUrl(), {
    ...options,
    method: "GET",
  });

  const body = [204, 205, 304].includes(res.status) ? null : await res.text();

  const data: listDirectoriesResponse["data"] = body ? JSON.parse(body) : {};
  return {
    data,
    status: res.status,
    headers: res.headers,
  } as listDirectoriesResponse;
};

export const getListDirectoriesQueryKey = () => {
  return [
    `https://api.workos.com/_widgets/directory-sync/directories`,
  ] as const;
};

export const getListDirectoriesQueryOptions = <
  TData = Awaited<ReturnType<typeof listDirectories>>,
  TError = ListDirectories403 | ListDirectories404,
>(options?: {
  query?: Partial<
    UseQueryOptions<Awaited<ReturnType<typeof listDirectories>>, TError, TData>
  >;
  fetch?: RequestInit;
}) => {
  const { query: queryOptions, fetch: fetchOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getListDirectoriesQueryKey();

  const queryFn: QueryFunction<Awaited<ReturnType<typeof listDirectories>>> = ({
    signal,
  }) => listDirectories({ signal, ...fetchOptions });

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof listDirectories>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type ListDirectoriesQueryResult = NonNullable<
  Awaited<ReturnType<typeof listDirectories>>
>;
export type ListDirectoriesQueryError = ListDirectories403 | ListDirectories404;

export function useListDirectories<
  TData = Awaited<ReturnType<typeof listDirectories>>,
  TError = ListDirectories403 | ListDirectories404,
>(
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof listDirectories>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listDirectories>>,
          TError,
          Awaited<ReturnType<typeof listDirectories>>
        >,
        "initialData"
      >;
    fetch?: RequestInit;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useListDirectories<
  TData = Awaited<ReturnType<typeof listDirectories>>,
  TError = ListDirectories403 | ListDirectories404,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof listDirectories>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listDirectories>>,
          TError,
          Awaited<ReturnType<typeof listDirectories>>
        >,
        "initialData"
      >;
    fetch?: RequestInit;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useListDirectories<
  TData = Awaited<ReturnType<typeof listDirectories>>,
  TError = ListDirectories403 | ListDirectories404,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof listDirectories>>,
        TError,
        TData
      >
    >;
    fetch?: RequestInit;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};

export function useListDirectories<
  TData = Awaited<ReturnType<typeof listDirectories>>,
  TError = ListDirectories403 | ListDirectories404,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof listDirectories>>,
        TError,
        TData
      >
    >;
    fetch?: RequestInit;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions = getListDirectoriesQueryOptions(options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData, TError> };

  return { ...query, queryKey: queryOptions.queryKey };
}

export type getDirectoryResponse200 = {
  data: Directory;
  status: 200;
};

export type getDirectoryResponse403 = {
  data: GetDirectory403;
  status: 403;
};

export type getDirectoryResponse404 = {
  data: GetDirectory404;
  status: 404;
};

export type getDirectoryResponseSuccess = getDirectoryResponse200 & {
  headers: Headers;
};
export type getDirectoryResponseError = (
  | getDirectoryResponse403
  | getDirectoryResponse404
) & {
  headers: Headers;
};

export type getDirectoryResponse =
  | getDirectoryResponseSuccess
  | getDirectoryResponseError;

export const getGetDirectoryUrl = (directoryId: string) => {
  return `https://api.workos.com/_widgets/directory-sync/directories/${directoryId}`;
};

export const getDirectory = async (
  directoryId: string,
  options?: RequestInit,
): Promise<getDirectoryResponse> => {
  const res = await fetch(getGetDirectoryUrl(directoryId), {
    ...options,
    method: "GET",
  });

  const body = [204, 205, 304].includes(res.status) ? null : await res.text();

  const data: getDirectoryResponse["data"] = body ? JSON.parse(body) : {};
  return {
    data,
    status: res.status,
    headers: res.headers,
  } as getDirectoryResponse;
};

export const getGetDirectoryQueryKey = (directoryId: string) => {
  return [
    `https://api.workos.com/_widgets/directory-sync/directories/${directoryId}`,
  ] as const;
};

export const getGetDirectoryQueryOptions = <
  TData = Awaited<ReturnType<typeof getDirectory>>,
  TError = GetDirectory403 | GetDirectory404,
>(
  directoryId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getDirectory>>, TError, TData>
    >;
    fetch?: RequestInit;
  },
) => {
  const { query: queryOptions, fetch: fetchOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getGetDirectoryQueryKey(directoryId);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getDirectory>>> = ({
    signal,
  }) => getDirectory(directoryId, { signal, ...fetchOptions });

  return {
    queryKey,
    queryFn,
    enabled: !!directoryId,
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<ReturnType<typeof getDirectory>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetDirectoryQueryResult = NonNullable<
  Awaited<ReturnType<typeof getDirectory>>
>;
export type GetDirectoryQueryError = GetDirectory403 | GetDirectory404;

export function useGetDirectory<
  TData = Awaited<ReturnType<typeof getDirectory>>,
  TError = GetDirectory403 | GetDirectory404,
>(
  directoryId: string,
  options: {
    query: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getDirectory>>, TError, TData>
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getDirectory>>,
          TError,
          Awaited<ReturnType<typeof getDirectory>>
        >,
        "initialData"
      >;
    fetch?: RequestInit;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetDirectory<
  TData = Awaited<ReturnType<typeof getDirectory>>,
  TError = GetDirectory403 | GetDirectory404,
>(
  directoryId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getDirectory>>, TError, TData>
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getDirectory>>,
          TError,
          Awaited<ReturnType<typeof getDirectory>>
        >,
        "initialData"
      >;
    fetch?: RequestInit;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetDirectory<
  TData = Awaited<ReturnType<typeof getDirectory>>,
  TError = GetDirectory403 | GetDirectory404,
>(
  directoryId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getDirectory>>, TError, TData>
    >;
    fetch?: RequestInit;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};

export function useGetDirectory<
  TData = Awaited<ReturnType<typeof getDirectory>>,
  TError = GetDirectory403 | GetDirectory404,
>(
  directoryId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getDirectory>>, TError, TData>
    >;
    fetch?: RequestInit;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions = getGetDirectoryQueryOptions(directoryId, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData, TError> };

  return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * Returns the widget settings for the current environment, including enabled authentication methods and branding configuration.
 */
export type settingsResponse200 = {
  data: SettingsResponse;
  status: 200;
};

export type settingsResponse403 = {
  data: Settings403;
  status: 403;
};

export type settingsResponseSuccess = settingsResponse200 & {
  headers: Headers;
};
export type settingsResponseError = settingsResponse403 & {
  headers: Headers;
};

export type settingsResponse = settingsResponseSuccess | settingsResponseError;

export const getSettingsUrl = () => {
  return `https://api.workos.com/_widgets/settings`;
};

export const settings = async (
  options?: RequestInit,
): Promise<settingsResponse> => {
  const res = await fetch(getSettingsUrl(), {
    ...options,
    method: "GET",
  });

  const body = [204, 205, 304].includes(res.status) ? null : await res.text();

  const data: settingsResponse["data"] = body ? JSON.parse(body) : {};
  return { data, status: res.status, headers: res.headers } as settingsResponse;
};

export const getSettingsQueryKey = () => {
  return [`https://api.workos.com/_widgets/settings`] as const;
};

export const getSettingsQueryOptions = <
  TData = Awaited<ReturnType<typeof settings>>,
  TError = Settings403,
>(options?: {
  query?: Partial<
    UseQueryOptions<Awaited<ReturnType<typeof settings>>, TError, TData>
  >;
  fetch?: RequestInit;
}) => {
  const { query: queryOptions, fetch: fetchOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getSettingsQueryKey();

  const queryFn: QueryFunction<Awaited<ReturnType<typeof settings>>> = ({
    signal,
  }) => settings({ signal, ...fetchOptions });

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof settings>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type SettingsQueryResult = NonNullable<
  Awaited<ReturnType<typeof settings>>
>;
export type SettingsQueryError = Settings403;

export function useSettings<
  TData = Awaited<ReturnType<typeof settings>>,
  TError = Settings403,
>(
  options: {
    query: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof settings>>, TError, TData>
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof settings>>,
          TError,
          Awaited<ReturnType<typeof settings>>
        >,
        "initialData"
      >;
    fetch?: RequestInit;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useSettings<
  TData = Awaited<ReturnType<typeof settings>>,
  TError = Settings403,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof settings>>, TError, TData>
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof settings>>,
          TError,
          Awaited<ReturnType<typeof settings>>
        >,
        "initialData"
      >;
    fetch?: RequestInit;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useSettings<
  TData = Awaited<ReturnType<typeof settings>>,
  TError = Settings403,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof settings>>, TError, TData>
    >;
    fetch?: RequestInit;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};

export function useSettings<
  TData = Awaited<ReturnType<typeof settings>>,
  TError = Settings403,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof settings>>, TError, TData>
    >;
    fetch?: RequestInit;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions = getSettingsQueryOptions(options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData, TError> };

  return { ...query, queryKey: queryOptions.queryKey };
}
